import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  adminAPI,
  AdminStatsResponse,
  AdminUserListItem,
  TimeSeriesPoint,
  UserDetailedStats,
  FeedbackItem,
  FeedbackEmailConfig,
  ADMIN_TOKEN_KEY,
  MetricsDashboard,
  RealtimeDocuments,
  FailureMetrics,
  CampaignStatsResponse,
  AIModelConfig,
  AIModelConfigUpdate,
} from '../../services/adminApi';
import {
  ResponsiveContainer,
  LineChart as RechartsLineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  RadialBarChart,
  RadialBar,
  PolarAngleAxis,
} from 'recharts';
import { SubscriptionTier } from '../../services/api';
import { TIER_FEATURES } from '../../constants/subscription';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  TrashIcon,
  HomeIcon,
  UserIcon,
  AlertCircleIcon,
  TrendingUpIcon,
  ChatIcon,
  ZapIcon,
  ShieldIcon,
  TargetIcon,
  SettingsIcon,
} from '../../components/Icons';
import './Admin.css';

const formatDate = (value: string) => {
  if (!value) return 'N/A';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('pt-BR');
};

const formatShortDate = (value: string) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

const safeTimestamp = (value?: string | null) => {
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const safeFixed = (value: unknown, decimals: number = 1) => {
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(decimals) : Number(0).toFixed(decimals);
};

const normalizeSource = (source?: string | null) => source || 'direct';
const displaySource = (source?: string | null) => normalizeSource(source).replace(/['"]/g, '').trim();
const safeDivide = (num: number, denom: number, factor = 1) => (denom ? (num / denom) * factor : 0);
const PALETTE = ['#0ea5e9', '#22c55e', '#f59e0b', '#8b5cf6', '#ef4444', '#14b8a6', '#e879f9', '#6366f1', '#f97316', '#06b6d4'];
// Recharts typings for PolarAngleAxis in our version return ReactNode; cast to component to satisfy TSX.
const PolarAxis = PolarAngleAxis as unknown as React.ComponentType<any>;

const LineChart: React.FC<{ title: string; data: TimeSeriesPoint[] }> = ({ title, data }) => (
  <div className="admin-card admin-chart">
    <div className="admin-card-header">
      <h3>{title}</h3>
    </div>
    <div className="admin-chart-body">
      {data.length === 0 && <div className="admin-empty">Sem dados ainda.</div>}
      {data.length > 0 && (
        <div className="admin-chart-canvas">
          <ResponsiveContainer width="100%" height={220}>
            <RechartsLineChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid stroke="#e2e8f0" strokeDasharray="4 4" />
              <XAxis dataKey="date" tickFormatter={formatShortDate} />
              <YAxis allowDecimals={false} />
              <Tooltip
                formatter={(value) => [`${value ?? 0}`, 'Total']}
                labelFormatter={(label: string) => `Data: ${formatDate(label)}`}
              />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#0c8b5c"
                strokeWidth={2.5}
                dot={{ r: 3, stroke: '#0c8b5c', strokeWidth: 1, fill: 'white' }}
                activeDot={{ r: 5 }}
              />
            </RechartsLineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  </div>
);

const AvgLineChart: React.FC<{ title: string; data: { date: string; average: number }[]; unit?: string }> = ({ title, data, unit }) => (
  <div className="admin-card admin-chart">
    <div className="admin-card-header">
      <h3>{title}</h3>
    </div>
    <div className="admin-chart-body">
      {data.length === 0 && <div className="admin-empty">Sem dados ainda.</div>}
      {data.length > 0 && (
        <div className="admin-chart-canvas">
          <ResponsiveContainer width="100%" height={220}>
            <RechartsLineChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid stroke="#e2e8f0" strokeDasharray="4 4" />
              <XAxis dataKey="date" tickFormatter={formatShortDate} />
              <YAxis allowDecimals />
              <Tooltip
                formatter={(value) => [`${value ?? 0}${unit ? ` ${unit}` : ''}`, 'Média']}
                labelFormatter={(label: string) => `Data: ${formatDate(label)}`}
              />
              <Line
                type="monotone"
                dataKey="average"
                stroke="#0a744d"
                strokeWidth={2.5}
                dot={{ r: 3, stroke: '#0a744d', strokeWidth: 1, fill: 'white' }}
                activeDot={{ r: 5 }}
              />
            </RechartsLineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  </div>
);

const buildEmptyMetricsDashboard = (period: number): MetricsDashboard => ({
  period_days: period,
  users: {
    total_all_time: 0,
    new_this_period: 0,
    active_this_period: 0,
    paid: 0,
  },
  registration: {
    total: 0,
    by_method: {
      google: 0,
      email: 0,
      google_pct: 0,
      email_pct: 0,
    },
    by_source: {},
    from_anonymous: 0,
    from_anonymous_pct: 0,
  },
  funnel: {
    visitors: 0,
    signups: 0,
    uploads: 0,
    questions: 0,
    pricing_clicks: 0,
    payments: 0,
    conversion_rates: {
      visitor_to_signup: 0,
      signup_to_upload: 0,
      upload_to_question: 0,
      question_to_pricing: 0,
      pricing_to_payment: 0,
      overall_conversion: 0,
    },
  },
  upload_funnel: {
    started: 0,
    role_selected: 0,
    file_selected: 0,
    upload_completed: 0,
    conversion_rates: {
      started_to_role: 0,
      role_to_file: 0,
      file_to_upload: 0,
      overall: 0,
    },
    dropoff_rates: {
      role_dropoff: 0,
      file_dropoff: 0,
      upload_dropoff: 0,
    },
  },
  post_upload_funnel: {
    uploaded: 0,
    viewed_risks: 0,
    asked_questions: 0,
    enabled_notifications: 0,
    conversion_rates: {
      upload_to_risks: 0,
      upload_to_questions: 0,
      upload_to_notifications: 0,
      risks_to_questions: 0,
      questions_to_notifications: 0,
    },
  },
  limit_encounter: {
    hit_limit: 0,
    saw_paywall: 0,
    clicked_pricing: 0,
    completed_payment: 0,
    conversion_rates: {
      limit_to_paywall: 0,
      limit_to_pricing: 0,
      limit_to_payment: 0,
      pricing_to_payment: 0,
    },
  },
  ai_quality: {
    average_ai_confidence: 0,
    average_user_rating: 0,
    analysis_completion_rate: 0,
    total_questions_asked: 0,
    total_analyses_started: 0,
    total_analyses_completed: 0,
  },
  failures: {
    total_failures: 0,
    failure_breakdown: {},
    upload_failures: 0,
    payment_failures: 0,
    user_abandons: 0,
    error_logs: [],
  },
  behavior: {
    signups: {
      total: 0,
      landing: 0,
      google: 0,
      from_anonymous: 0,
      landing_pct: 0,
      google_pct: 0,
      anonymous_pct: 0,
    },
    cta_clicks: {
      total: 0,
      by_location: {},
    },
    anonymous: {
      sessions_started: 0,
      sessions_completed: 0,
      sessions_converted: 0,
      sessions_no_followup: 0,
      completion_rate: 0,
      conversion_rate: 0,
    },
    paid: {
      paid_users_total: 0,
      paid_users_new: 0,
      paid_upgrades: 0,
      paid_conversion_rate: 0,
    },
    tiers: {
      free: 0,
      premium: 0,
      platinum: 0,
    },
  },
  traffic: {
    total: 0,
    by_source: {},
    by_page: {},
    by_country: {},
    by_region: {},
    by_city: {},
    by_browser: {},
    by_os: {},
    by_device: {},
    window_days: period,
    page_filter: null,
    avg_visits_per_user_by_page: {},
  },
  mvp_metrics: {
    users_uploading_contract_pct: 0,
    users_asking_questions_pct: 0,
    users_clicking_pricing_pct: 0,
    users_willing_to_pay_pct: 0,
    average_ai_confidence: 0,
    analysis_completion_rate_pct: 0,
  },
  goals: {
    users_uploading_contract_target: 0,
    users_asking_questions_target: 0,
    users_clicking_pricing_target: 0,
    users_willing_to_pay_target: 0,
    average_ai_confidence_target: 0,
    analysis_completion_rate_target: 0,
  },
  insights: {
    problem_is_real: false,
    product_solving_it: false,
    users_will_pay: false,
  },
});

const normalizeMetricsDashboard = (data: MetricsDashboard | null, period: number): MetricsDashboard => {
  const base = buildEmptyMetricsDashboard(period);
  if (!data) return base;

  const bySource = data.registration?.by_source ?? base.registration.by_source;
  const bySourceTotal = Object.values(bySource).reduce((sum, val) => sum + (typeof val === 'number' ? val : 0), 0);
  const registrationTotal = (data.registration?.total ?? (bySourceTotal || 0));

  return {
    ...base,
    ...data,
    period_days: data.period_days ?? period,
    users: {
      ...base.users,
      ...(data.users ?? {}),
    },
    registration: {
      ...base.registration,
      ...(data.registration ?? {}),
      total: registrationTotal,
      by_method: {
        ...base.registration.by_method,
        ...(data.registration?.by_method ?? {}),
      },
      by_source: bySource,
      from_anonymous: data.registration?.from_anonymous ?? base.registration.from_anonymous,
      from_anonymous_pct: data.registration?.from_anonymous_pct ?? base.registration.from_anonymous_pct,
    },
    funnel: {
      ...base.funnel,
      ...(data.funnel ?? {}),
      conversion_rates: {
        ...base.funnel.conversion_rates,
        ...(data.funnel?.conversion_rates ?? {}),
      },
    },
    post_upload_funnel: {
      ...base.post_upload_funnel,
      ...(data.post_upload_funnel ?? {}),
      conversion_rates: {
        ...base.post_upload_funnel.conversion_rates,
        ...(data.post_upload_funnel?.conversion_rates ?? {}),
      },
    },
    limit_encounter: {
      ...base.limit_encounter,
      ...(data.limit_encounter ?? {}),
      conversion_rates: {
        ...base.limit_encounter.conversion_rates,
        ...(data.limit_encounter?.conversion_rates ?? {}),
      },
    },
    ai_quality: {
      ...base.ai_quality,
      ...(data.ai_quality ?? {}),
    },
    failures: {
      ...base.failures,
      ...(data.failures ?? {}),
      failure_breakdown: data.failures?.failure_breakdown ?? base.failures.failure_breakdown,
    },
    behavior: {
      ...base.behavior,
      ...(data.behavior ?? {}),
      signups: {
        ...base.behavior.signups,
        ...(data.behavior?.signups ?? {}),
      },
      anonymous: {
        ...base.behavior.anonymous,
        ...(data.behavior?.anonymous ?? {}),
      },
      paid: {
        ...base.behavior.paid,
        ...(data.behavior?.paid ?? {}),
      },
      tiers: {
        ...base.behavior.tiers,
        ...(data.behavior?.tiers ?? {}),
      },
    },
    traffic: {
      ...base.traffic,
      ...(data.traffic ?? {}),
    },
    mvp_metrics: {
      ...base.mvp_metrics,
      ...(data.mvp_metrics ?? {}),
    },
    goals: {
      ...base.goals,
      ...(data.goals ?? {}),
    },
    insights: {
      ...base.insights,
      ...(data.insights ?? {}),
    },
  };
};

const safePercentage = (num: number, denom: number) => (denom > 0 ? (num / denom) * 100 : 0);

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<AdminStatsResponse | null>(null);
  const [realtimeDocs, setRealtimeDocs] = useState<RealtimeDocuments | null>(null);
  const [users, setUsers] = useState<AdminUserListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'admins' | 'campaigns' | 'feedbacks' | 'performance' | 'metrics' | 'errors'>('overview');
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [updatingTier, setUpdatingTier] = useState(false);
  const [expandedUserId, setExpandedUserId] = useState<number | null>(null);
  const [userStats, setUserStats] = useState<Record<number, UserDetailedStats>>({});
  const [loadingStats, setLoadingStats] = useState<Record<number, boolean>>({});
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [processingTableLimit, setProcessingTableLimit] = useState<number>(100);

  // Feedback state
  const [feedbacks, setFeedbacks] = useState<FeedbackItem[]>([]);
  const [emailConfigs, setEmailConfigs] = useState<FeedbackEmailConfig[]>([]);
  const [loadingFeedbacks, setLoadingFeedbacks] = useState(false);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [addingEmail, setAddingEmail] = useState(false);
  const [attachmentViewerOpen, setAttachmentViewerOpen] = useState(false);
  const [attachmentViewerUrl, setAttachmentViewerUrl] = useState<string | null>(null);
  const [attachmentViewerFilename, setAttachmentViewerFilename] = useState('');
  const realtimeTimer = useRef<NodeJS.Timeout | null>(null);
  const [attachmentViewerIsImage, setAttachmentViewerIsImage] = useState(false);

  // Metrics state
  const [metricsDashboard, setMetricsDashboard] = useState<MetricsDashboard | null>(null);
  const [metricsExpanded, setMetricsExpanded] = useState<Record<string, boolean>>({
    users: true,
    language: false,
    registration: false,
    traffic: false,
    screen_time: false,
    post_upload: false,
    limit: false,
  });
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [globalPeriodDays, setGlobalPeriodDays] = useState<number>(30);
  const [failureMetrics, setFailureMetrics] = useState<FailureMetrics | null>(null);
  const [campaigns, setCampaigns] = useState<CampaignStatsResponse | null>(null);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [hiddenCampaigns, setHiddenCampaigns] = useState<string[]>([]);
  const [showHiddenCampaigns, setShowHiddenCampaigns] = useState(false);
  const [autoRefreshAll, setAutoRefreshAll] = useState<boolean>(false);
  const errorTimer = useRef<NodeJS.Timeout | null>(null);
  const [logLines, setLogLines] = useState<string[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logLimit, setLogLimit] = useState(200);
  const [logLevelFilter, setLogLevelFilter] = useState<string | undefined>(undefined);
  const [expandedErrorRows, setExpandedErrorRows] = useState<Set<number>>(new Set());
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [realtimeIntervalMs, setRealtimeIntervalMs] = useState<number>(5000);
  const [aiModelConfig, setAiModelConfig] = useState<AIModelConfig | null>(null);
  const [modelConfigForm, setModelConfigForm] = useState<AIModelConfigUpdate>({});
  const [baselineModelForm, setBaselineModelForm] = useState<AIModelConfigUpdate>({
    model_summary: '',
    model_risks: '',
    model_qa: '',
    model_contract_type: '',
    model_notifications: '',
  });
  const [loadingModelConfig, setLoadingModelConfig] = useState(false);
  const [savingModelConfig, setSavingModelConfig] = useState(false);
  const [modelConfigMessage, setModelConfigMessage] = useState<string | null>(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  const loadStats = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [statsRes, failuresRes] = await Promise.all([
        adminAPI.stats(processingTableLimit, globalPeriodDays),
        adminAPI.getFailureMetrics(globalPeriodDays),
      ]);
      setStats(statsRes.data);
      setFailureMetrics(failuresRes.data);
    } catch (err) {
      setError('Falha ao carregar métricas.');
      if (localStorage.getItem(ADMIN_TOKEN_KEY)) {
        localStorage.removeItem(ADMIN_TOKEN_KEY);
      }
      navigate('/admin/login');
    } finally {
      setLoading(false);
    }
  }, [navigate, processingTableLimit, globalPeriodDays]);

  const loadFeedbacks = useCallback(async () => {
    setLoadingFeedbacks(true);
    try {
      const [feedbacksRes, configsRes] = await Promise.all([
        adminAPI.getFeedbacks(showUnreadOnly),
        adminAPI.getFeedbackEmailConfigs(),
      ]);
      setFeedbacks(feedbacksRes.data);
      setEmailConfigs(configsRes.data);
    } catch (err) {
      console.error('Failed to load feedbacks:', err);
    } finally {
      setLoadingFeedbacks(false);
    }
  }, [showUnreadOnly]);

  const loadMetrics = useCallback(async () => {
    setLoadingMetrics(true);
    try {
      const res = await adminAPI.getMetricsDashboard(globalPeriodDays);
      setMetricsDashboard(normalizeMetricsDashboard(res.data, globalPeriodDays));
    } catch (err) {
      console.error('Failed to load metrics:', err);
    } finally {
      setLoadingMetrics(false);
    }
  }, [globalPeriodDays]);

  const loadCampaigns = useCallback(async () => {
    setLoadingCampaigns(true);
    try {
      const res = await adminAPI.getCampaigns(globalPeriodDays);
      setCampaigns(res.data);
      const hidden = (res.data?.campaigns || []).filter((c) => c.is_hidden).map((c) => normalizeSource(c.source));
      setHiddenCampaigns(hidden);
    } catch (err) {
      console.error('Failed to load campaigns', err);
      setError('Falha ao carregar campanhas. Verifique a conexão.');
      setCampaigns(null);
    } finally {
      setLoadingCampaigns(false);
    }
  }, [globalPeriodDays]);

  const loadLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const res = await adminAPI.getLogs(logLimit, logLevelFilter);
      setLogLines(res.data.lines || []);
    } catch (err) {
      console.error('Failed to load logs', err);
    } finally {
      setLoadingLogs(false);
    }
  }, [logLimit, logLevelFilter]);

  const syncModelForm = useCallback((data: AIModelConfig) => {
    const normalized: AIModelConfigUpdate = {
      model_summary: data.model_summary ?? '',
      model_risks: data.model_risks ?? '',
      model_qa: data.model_qa ?? '',
      model_contract_type: data.model_contract_type ?? '',
      model_notifications: data.model_notifications ?? '',
    };
    setModelConfigForm(normalized);
    setBaselineModelForm(normalized);
  }, []);

  const loadAIModelConfig = useCallback(async () => {
    setLoadingModelConfig(true);
    setModelConfigMessage(null);
    try {
      const res = await adminAPI.getAIModelConfig();
      setAiModelConfig(res.data);
      syncModelForm(res.data);
    } catch (err) {
      console.error('Failed to load AI model config', err);
      setModelConfigMessage('Falha ao carregar configurações.');
    } finally {
      setLoadingModelConfig(false);
    }
  }, [syncModelForm]);

  const loadRealtimeDocs = useCallback(async () => {
    try {
      const res = await adminAPI.getRealtimeDocuments(globalPeriodDays);
      setRealtimeDocs(res.data);
    } catch (err) {
      console.error('Failed to fetch realtime documents', err);
    }
  }, [globalPeriodDays]);

  const handleMarkAsRead = async (feedbackId: number) => {
    try {
      await adminAPI.markFeedbackRead(feedbackId);
      setFeedbacks(feedbacks.map(f =>
        f.id === feedbackId ? { ...f, is_read: true } : f
      ));
    } catch (err) {
      console.error('Failed to mark feedback as read:', err);
    }
  };

  const handleDeleteFeedback = async (feedbackId: number) => {
    if (!window.confirm('Tem certeza que deseja deletar este feedback?')) {
      return;
    }

    try {
      await adminAPI.deleteFeedback(feedbackId);
      setFeedbacks(feedbacks.filter(f => f.id !== feedbackId));
    } catch (err) {
      console.error('Failed to delete feedback:', err);
      alert('Erro ao deletar feedback');
    }
  };

  const closeAttachmentViewer = () => {
    if (attachmentViewerUrl) {
      URL.revokeObjectURL(attachmentViewerUrl);
    }
    setAttachmentViewerOpen(false);
    setAttachmentViewerUrl(null);
    setAttachmentViewerFilename('');
    setAttachmentViewerIsImage(false);
  };

  const formatBytes = (bytes?: number | null) => {
    if (!bytes || bytes <= 0) return '';
    const units = ['B', 'KB', 'MB', 'GB'];
    let value = bytes;
    let unitIndex = 0;
    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex += 1;
    }
    const decimals = unitIndex === 0 ? 0 : value < 10 ? 1 : 0;
    return `${value.toFixed(decimals)} ${units[unitIndex]}`;
  };

  const handleViewFeedbackAttachment = async (feedback: FeedbackItem, attachmentId?: number) => {
    try {
      const res = attachmentId
        ? await adminAPI.getFeedbackAttachmentById(feedback.id, attachmentId)
        : await adminAPI.getFeedbackAttachment(feedback.id);
      const blob = res.data as Blob;
      const url = URL.createObjectURL(blob);
      setAttachmentViewerUrl(url);
      const picked = attachmentId
        ? feedback.attachments?.find((a) => a.id === attachmentId)
        : feedback.attachments?.[0];
      setAttachmentViewerFilename(picked?.attachment_filename || feedback.attachment_filename || 'anexo');
      const contentType = picked?.attachment_content_type || feedback.attachment_content_type || blob.type || '';
      setAttachmentViewerIsImage(contentType.startsWith('image/'));
      setAttachmentViewerOpen(true);
    } catch (err) {
      console.error('Failed to load attachment:', err);
      alert('Falha ao carregar o anexo.');
    }
  };

  const handleAddEmail = async () => {
    if (!newEmail.trim()) return;

    setAddingEmail(true);
    try {
      const res = await adminAPI.addFeedbackEmailConfig(newEmail.trim());
      setEmailConfigs([...emailConfigs, res.data]);
      setNewEmail('');
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Erro ao adicionar e-mail');
    } finally {
      setAddingEmail(false);
    }
  };

  const handleToggleEmailActive = async (configId: number, isActive: boolean) => {
    try {
      await adminAPI.updateFeedbackEmailConfig(configId, isActive);
      setEmailConfigs(emailConfigs.map(c =>
        c.id === configId ? { ...c, is_active: isActive } : c
      ));
    } catch (err) {
      console.error('Failed to update email config:', err);
    }
  };

  const handleDeleteEmail = async (configId: number) => {
    if (!window.confirm('Remover este e-mail da lista de notificações?')) return;

    try {
      await adminAPI.deleteFeedbackEmailConfig(configId);
      setEmailConfigs(emailConfigs.filter(c => c.id !== configId));
    } catch (err) {
      console.error('Failed to delete email config:', err);
    }
  };

  const handleHideCampaign = useCallback((source: string | null | undefined) => {
    const key = normalizeSource(source);
    adminAPI.hideCampaign(key).then(() => {
      setHiddenCampaigns((prev) => [...prev, key]);
      // refresh to get is_hidden flags
      loadCampaigns();
    }).catch((err) => console.error('Failed to hide campaign', err));
  }, [loadCampaigns]);

  const handleUnhideCampaign = useCallback((source: string | null | undefined) => {
    const key = normalizeSource(source);
    adminAPI.unhideCampaign(key).then(() => {
      setHiddenCampaigns((prev) => prev.filter((s) => s !== key));
      loadCampaigns();
    }).catch((err) => console.error('Failed to unhide campaign', err));
  }, [loadCampaigns]);

  const renderTopList = (title: string, data: Record<string, number>, emptyLabel = 'Sem dados') => {
    const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]).slice(0, 6);
    return (
      <div className="admin-card">
        <h4 style={{ marginBottom: '12px', fontSize: '16px', fontWeight: '600' }}>{title}</h4>
        {entries.length === 0 ? (
          <div className="admin-empty">{emptyLabel}</div>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {entries.map(([label, value]) => (
              <li key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                <span style={{ color: '#475569' }}>{label || 'Desconhecido'}</span>
                <strong style={{ color: '#0f172a' }}>{value}</strong>
              </li>
            ))}
          </ul>
        )}

      </div>
    );
  };

  const avgPageVisitsData = useMemo(() => {
    if (!metricsDashboard) return [];
    const entries = Object.entries(metricsDashboard.traffic.avg_visits_per_user_by_page || {});
    return entries
      .map(([page, avg]) => ({
        page: (page || 'Desconhecido').replace(/"/g, ''),
        avg: Number(avg) || 0,
      }))
      .sort((a, b) => b.avg - a.avg)
      .slice(0, 8);
  }, [metricsDashboard]);

  const toggleMetricsSection = (key: string) =>
    setMetricsExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const renderMetricsSection = (key: string, title: string, content: React.ReactNode) => (
    <div style={{ marginBottom: '10px', border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', background: '#ffffff' }}>
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 18px', background: metricsExpanded[key] ? '#f8fafc' : '#ffffff', cursor: 'pointer', userSelect: 'none', borderBottom: metricsExpanded[key] ? '1px solid #e2e8f0' : 'none', transition: 'background 0.15s' }}
        onClick={() => toggleMetricsSection(key)}
        role="button"
      >
        <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#1e293b', letterSpacing: '-0.01em' }}>{title}</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: metricsExpanded[key] ? '#3b82f6' : '#94a3b8', fontSize: '12px', fontWeight: '500' }}>
          <span>{metricsExpanded[key] ? 'Recolher' : 'Expandir'}</span>
          {metricsExpanded[key] ? <ChevronUpIcon size={14} /> : <ChevronDownIcon size={14} />}
        </div>
      </div>
      {metricsExpanded[key] && <div style={{ padding: '20px' }}>{content}</div>}
    </div>
  );

  const hiddenCampaignSet = useMemo(() => new Set(hiddenCampaigns), [hiddenCampaigns]);

  const visibleCampaigns = useMemo(() => {
    const list = campaigns?.campaigns || [];
    if (showHiddenCampaigns) return list;
    return list.filter((c) => !hiddenCampaignSet.has(normalizeSource(c.source)) && !c.is_hidden);
  }, [campaigns, showHiddenCampaigns, hiddenCampaignSet]);

  const campaignAggregates = useMemo(() => {
    const list = visibleCampaigns;
    const totalVisitors = list.reduce((sum, c) => sum + (c.visitors || 0), 0);
    const totalRegistrations = list.reduce((sum, c) => sum + (c.registered_users || 0), 0);
    const totalUploads = list.reduce((sum, c) => sum + (c.registered_uploads || 0), 0);
    const totalPaid = list.reduce((sum, c) => sum + (c.paid_premium || 0) + (c.paid_platinum || 0), 0);
    return {
      totalVisitors,
      totalRegistrations,
      totalUploads,
      totalPaid,
      convVisitToReg: safeDivide(totalRegistrations, totalVisitors, 100),
      convRegToUpload: safeDivide(totalUploads, totalRegistrations, 100),
      convUploadToPaid: safeDivide(totalPaid, totalUploads, 100),
    };
  }, [visibleCampaigns]);

  const campaignChartsData = useMemo(() => {
    const list = visibleCampaigns;
    const sortedByConversion = [...list].sort((a, b) => (b.visitor_to_registration ?? 0) - (a.visitor_to_registration ?? 0)).slice(0, 10);
    const conversionBars = sortedByConversion.map((c) => ({
      name: displaySource(c.source),
      visitToReg: c.visitor_to_registration || 0,
      regToUpload: c.registration_to_upload || 0,
      uploadToPaid: c.upload_to_paid || 0,
    }));

    const uploadsPerVisitor = list
      .map((c) => ({
        name: displaySource(c.source),
        uploadsPerVisitor: safeDivide(c.registered_uploads || 0, c.visitors || 0, 1),
      }))
      .filter((d) => Number.isFinite(d.uploadsPerVisitor))
      .sort((a, b) => b.uploadsPerVisitor - a.uploadsPerVisitor)
      .slice(0, 10);

    const anonymousUploadsPerCampaign = list
      .map((c) => ({
        name: displaySource(c.source),
        anonymousUploads: c.anonymous_uploads || 0,
      }))
      .filter((d) => Number.isFinite(d.anonymousUploads))
      .sort((a, b) => b.anonymousUploads - a.anonymousUploads)
      .slice(0, 10);

    const paidMix = [...list]
      .sort((a, b) => (b.paid_premium + b.paid_platinum) - (a.paid_premium + a.paid_platinum))
      .slice(0, 8)
      .map((c) => ({
        name: displaySource(c.source),
        premium: c.paid_premium || 0,
        platinum: c.paid_platinum || 0,
      }));

    const trafficShare = () => {
      const total = list.reduce((sum, c) => sum + (c.visitors || 0), 0);
      if (!total) return [];
      const top = [...list].sort((a, b) => (b.visitors || 0) - (a.visitors || 0)).slice(0, 7);
      const topTotal = top.reduce((sum, c) => sum + (c.visitors || 0), 0);
      const others = total - topTotal;
      const pie = top.map((c) => ({
        name: displaySource(c.source),
        value: c.visitors || 0,
      }));
      if (others > 0) pie.push({ name: 'Outros', value: others });
      return pie;
    };

    return {
      conversionBars,
      uploadsPerVisitor,
      anonymousUploadsPerCampaign,
      paidMix,
      trafficShare: trafficShare(),
    };
  }, [visibleCampaigns]);

  const campaignVisitsOverTime = useMemo(() => {
    if (campaigns?.visits_over_time?.length) return campaigns.visits_over_time;
    if (campaignAggregates.totalVisitors > 0) {
      const fallbackDate = campaigns?.generated_at || new Date().toISOString();
      return [{ date: fallbackDate, count: campaignAggregates.totalVisitors }];
    }
    return [];
  }, [campaigns, campaignAggregates.totalVisitors]);

  const campaignVisitsByHour = useMemo(() => {
    const points = campaigns?.hourly_avg_visits || [];
    if (points.length > 0) {
      return points.map((p) => ({
        hour: p.hour,
        label: `${String(p.hour).padStart(2, '0')}h`,
        avg: Number(p.average_users ?? p.avg ?? p.count ?? 0),
      }));
    }
    if (campaignAggregates.totalVisitors > 0) {
      return [
        {
          hour: 0,
          label: 'total',
          avg: campaignAggregates.totalVisitors,
        },
      ];
    }
    return [];
  }, [campaigns, campaignAggregates.totalVisitors]);

  // Refresh data when global period changes for period-sensitive tabs
  useEffect(() => {
    if (activeTab === 'metrics') {
      loadMetrics();
    }
    if (activeTab === 'campaigns') {
      loadCampaigns();
    }
  }, [globalPeriodDays, activeTab, loadMetrics, loadCampaigns]);

  // Refresh stats when table limit changes on performance tab
  useEffect(() => {
    if (activeTab === 'performance') {
      loadStats();
    }
  }, [processingTableLimit, activeTab, loadStats]);

  useEffect(() => {
    if (activeTab === 'errors') {
      loadLogs();
    }
  }, [activeTab, loadLogs, logLevelFilter]);

  const loadUsers = useCallback(async () => {
    try {
      const res = await adminAPI.users();
      setUsers(res.data.users);
    } catch (err) {
      setError('Falha ao carregar usuários.');
    }
  }, []);

  const refreshAll = useCallback(() => {
    loadStats();
    loadUsers();
    loadFeedbacks();
    loadMetrics();
    loadCampaigns();
    setLastUpdated(new Date().toISOString());
    if (activeTab === 'errors') {
      loadLogs();
    }
  }, [loadStats, loadUsers, loadFeedbacks, loadMetrics, loadCampaigns, loadLogs, activeTab]);

  // Refresh tab data immediately on tab switch
  useEffect(() => {
    switch (activeTab) {
      case 'overview':
        loadStats();
        loadRealtimeDocs();
        break;
      case 'users':
        loadStats();
        loadUsers();
        break;
      case 'admins':
        loadStats();
        break;
      case 'performance':
        loadStats();
        loadRealtimeDocs();
        break;
      case 'campaigns':
        loadCampaigns();
        break;
      case 'feedbacks':
        loadFeedbacks();
        break;
      case 'metrics':
        loadMetrics();
        break;
      case 'errors':
        loadStats();
        loadLogs();
        setFailureMetrics(null);
        adminAPI.getFailureMetrics(globalPeriodDays).then((res) => setFailureMetrics(res.data)).catch(() => {});
        break;
      default:
        break;
    }
  }, [activeTab, loadStats, loadUsers, loadFeedbacks, loadRealtimeDocs, loadLogs, loadMetrics, loadCampaigns, globalPeriodDays]);

  useEffect(() => {
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (!token) {
      navigate('/admin/login');
      return;
    }
    refreshAll();
    loadRealtimeDocs();

    realtimeTimer.current = setInterval(loadRealtimeDocs, realtimeIntervalMs);
    if (autoRefreshAll) {
      errorTimer.current = setInterval(() => {
        refreshAll();
      }, 15000);
    }
    return () => {
      if (realtimeTimer.current) clearInterval(realtimeTimer.current);
      if (errorTimer.current) clearInterval(errorTimer.current);
    };
  }, [refreshAll, loadRealtimeDocs, navigate, autoRefreshAll, realtimeIntervalMs]);

  const modelOptions = useMemo(() => {
    const hierarchy: Record<string, number> = {
      'gpt-4.1': 1,
      'gpt-4o': 2,
      'o1-mini': 3,
      'gpt-4.1-mini': 4,
      'gpt-4o-mini': 5,
    };
    const base = aiModelConfig?.allowed_models ?? ['gpt-4.1', 'gpt-4o', 'o1-mini', 'gpt-4.1-mini', 'gpt-4o-mini'];
    return [...base].sort((a, b) => (hierarchy[a] || 99) - (hierarchy[b] || 99));
  }, [aiModelConfig]);

  const defaultModels = aiModelConfig?.default_models || {};
  const defaultLabelFor = (key: keyof AIModelConfigUpdate) => {
    const envDefault =
      (defaultModels as Record<string, string | undefined>)[key as string] ||
      modelOptions[0];
    return envDefault || '';
  };

  const hasModelChanges = useMemo(
    () =>
      (['model_summary', 'model_risks', 'model_qa', 'model_contract_type', 'model_notifications'] as Array<
        keyof AIModelConfigUpdate
      >).some((key) => (modelConfigForm[key] || '') !== (baselineModelForm[key] || '')),
    [modelConfigForm, baselineModelForm]
  );

  const modelSections = useMemo(
    () => [
      {
        title: 'Processamento do contrato',
        items: [
          { key: 'model_contract_type' as const, label: 'Verificação de Tipo', helper: 'Detecta se o documento é contrato de locação.' },
          { key: 'model_summary' as const, label: 'Resumo e Partes', helper: 'Usado no Stage 1 rápido e resumo estruturado.' },
          { key: 'model_risks' as const, label: 'Riscos + Recomendações', helper: 'Detecção detalhada de riscos e sugestões.' },
        ],
      },
      {
        title: 'Outros',
        items: [
          { key: 'model_qa' as const, label: 'Perguntas (Q&A)', helper: 'Respostas em tempo real sobre o contrato.' },
          { key: 'model_notifications' as const, label: 'Notificações', helper: 'Extração de eventos e datas para lembretes.' },
        ],
      },
    ],
    []
  );

  const handleModelChange = (key: keyof AIModelConfigUpdate, value: string) => {
    setModelConfigForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleResetModelConfig = () => {
    setModelConfigForm({
      model_summary: '',
      model_risks: '',
      model_qa: '',
      model_contract_type: '',
      model_notifications: '',
    });
    setModelConfigMessage('Campos limpos. Salve para voltar ao padrão do backend (.env).');
  };

  const handleSaveModelConfig = async () => {
    setSavingModelConfig(true);
    setModelConfigMessage(null);
    try {
      const normalize = (v?: string | null) => (v === '' || v == null ? null : v);
      const payload: AIModelConfigUpdate = {
        model_summary: normalize(modelConfigForm.model_summary),
        model_risks: normalize(modelConfigForm.model_risks),
        model_qa: normalize(modelConfigForm.model_qa),
        model_contract_type: normalize(modelConfigForm.model_contract_type),
        model_notifications: normalize(modelConfigForm.model_notifications),
      };
      const res = await adminAPI.updateAIModelConfig(payload);
      setAiModelConfig(res.data);
      syncModelForm(res.data);
      setModelConfigMessage('Modelos atualizados com sucesso.');
    } catch (err) {
      console.error('Failed to save AI model config', err);
      setModelConfigMessage('Falha ao salvar. Verifique se o modelo é permitido.');
    } finally {
      setSavingModelConfig(false);
    }
  };

  const closeSettingsModal = () => {
    setShowSettingsModal(false);
    setModelConfigMessage(null);
  };

  useEffect(() => {
    if (!modelConfigMessage) return;
    const timer = setTimeout(() => setModelConfigMessage(null), 3200);
    return () => clearTimeout(timer);
  }, [modelConfigMessage]);

  const handleLogout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem('admin_username');
    navigate('/admin/login');
  };

  const handleUpdateTier = async (userId: number, newTier: string) => {
    setUpdatingTier(true);
    try {
      await adminAPI.updateUserTier(userId, newTier);
      // Refresh users list
      await loadUsers();
      setEditingUserId(null);
    } catch (err) {
      alert('Falha ao atualizar o plano do usuário.');
    } finally {
      setUpdatingTier(false);
    }
  };

  const getTierBadgeClass = (tier: string) => {
    switch (tier) {
      case SubscriptionTier.PREMIUM:
        return 'tier-badge-premium';
      case SubscriptionTier.PLATINUM:
        return 'tier-badge-platinum';
      default:
        return 'tier-badge-free';
    }
  };

  const getTierName = (tier: string) => {
    const tierKey = tier as SubscriptionTier;
    return TIER_FEATURES[tierKey]?.name || tier;
  };

  const handleToggleUserExpand = async (userId: number) => {
    if (expandedUserId === userId) {
      setExpandedUserId(null);
      return;
    }

    setExpandedUserId(userId);

    // Lazy load user stats if not already loaded
    if (!userStats[userId]) {
      setLoadingStats({ ...loadingStats, [userId]: true });
      try {
        const response = await adminAPI.getUserStats(userId);
        setUserStats({ ...userStats, [userId]: response.data });
      } catch (err) {
        alert('Falha ao carregar estatísticas do usuário.');
      } finally {
        setLoadingStats({ ...loadingStats, [userId]: false });
      }
    }
  };

  const handleDeleteUser = async (userId: number) => {
    if (deleteConfirm !== userId) {
      setDeleteConfirm(userId);
      return;
    }

    setDeleting(true);
    try {
      await adminAPI.deleteUser(userId);
      await loadUsers();
      setDeleteConfirm(null);
      setExpandedUserId(null);
    } catch (err) {
      alert('Falha ao deletar o usuário.');
    } finally {
      setDeleting(false);
    }
  };

  const adminName = localStorage.getItem('admin_username') || 'Administrador';

  const formatDateTime = (value?: string | null) => {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleString('pt-BR');
  };

  const formatDate = (value?: string | null) => {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString('pt-BR');
  };

  const formatSeconds = (value?: number | null) => {
    if (value === null || value === undefined) return '—';
    return value.toFixed(2);
  };

  const formatMsToSeconds = (value?: number | null) => {
    if (value === null || value === undefined) return '—';
    return (value / 1000).toFixed(2);
  };

  const formatMs = (value?: number | null) => {
    if (value === null || value === undefined) return '—';
    return `${Math.round(value)} ms`;
  };

  const toggleErrorRow = (idx: number) => {
    setExpandedErrorRows((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  const renderFailureBreakdown = () => {
    if (!failureMetrics) {
      return <div className="admin-empty">Sem dados de erros.</div>;
    }
    const breakdown = failureMetrics.failure_breakdown || {};
    const entries = Object.entries(breakdown);
    const categories = failureMetrics.category_counts || {};
    return (
      <>
        <div className="admin-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
          <div className="admin-card">
            <span className="admin-metric-label">Total de erros (30d)</span>
            <strong className="admin-metric-value">{failureMetrics.total_failures}</strong>
          </div>
          <div className="admin-card">
            <span className="admin-metric-label">Uploads com erro</span>
            <strong className="admin-metric-value">{failureMetrics.upload_failures}</strong>
          </div>
          <div className="admin-card">
            <span className="admin-metric-label">Erros de pagamento</span>
            <strong className="admin-metric-value">{failureMetrics.payment_failures}</strong>
          </div>
          <div className="admin-card">
            <span className="admin-metric-label">Abandonos</span>
            <strong className="admin-metric-value">{failureMetrics.user_abandons}</strong>
          </div>
        </div>

        <div className="admin-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', marginTop: '12px' }}>
          <div className="admin-card">
            <span className="admin-metric-label">Erros externos</span>
            <strong className="admin-metric-value">{categories.external ?? 0}</strong>
          </div>
          <div className="admin-card">
            <span className="admin-metric-label">Erros internos (500)</span>
            <strong className="admin-metric-value">{categories.internal ?? 0}</strong>
          </div>
          <div className="admin-card">
            <span className="admin-metric-label">Erros de usuário (400)</span>
            <strong className="admin-metric-value">{categories.user ?? 0}</strong>
          </div>
          <div className="admin-card">
            <span className="admin-metric-label">Erros de upload</span>
            <strong className="admin-metric-value">{categories.upload ?? 0}</strong>
          </div>
        </div>

        {failureMetrics.failure_codes_over_time && failureMetrics.failure_codes_over_time.length > 0 && (
          <div className="admin-card admin-chart" style={{ marginTop: '16px' }}>
            <div className="admin-card-header">
              <h3>Erros por código ao longo do tempo</h3>
            </div>
            <div className="admin-chart-body">
              <div className="admin-chart-canvas">
                <ResponsiveContainer width="100%" height={240}>
                  <RechartsLineChart data={failureMetrics.failure_codes_over_time} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid stroke="#e2e8f0" strokeDasharray="4 4" />
                    <XAxis dataKey="date" tickFormatter={formatShortDate} />
                    <YAxis allowDecimals={false} />
                    <Tooltip
                      formatter={(value, name) => [`${value ?? 0}`, name]}
                      labelFormatter={(label: string) => `Data: ${formatDate(label)}`}
                    />
                    <Line type="monotone" dataKey="internal" name="500 / Internos" stroke="#ef4444" strokeWidth={2.2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="user" name="400 / Usuário" stroke="#f59e0b" strokeWidth={2.2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="upload" name="Upload" stroke="#3b82f6" strokeWidth={2.2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="external" name="Externos" stroke="#10b981" strokeWidth={2.2} dot={{ r: 3 }} />
                  </RechartsLineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {entries.length > 0 && (
          <div className="admin-card" style={{ marginTop: '16px' }}>
            <span className="admin-metric-label">Detalhes por motivo</span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px' }}>
              {entries.map(([reason, count]) => (
                <div key={reason} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                  <span style={{ color: '#475569' }}>{reason || 'desconhecido'}</span>
                  <strong style={{ color: '#0f172a' }}>{count as number}</strong>
                </div>
              ))}
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="admin-page">
      <div className="admin-topbar">
        <button className="admin-topbar-button" onClick={() => navigate('/')}>
          Landing Page
        </button>
        <button className="admin-topbar-button danger" onClick={handleLogout}>
          Sair
        </button>
      </div>
      <div className="admin-shell">
        <header className="admin-header">
          <div>
            <p className="admin-kicker">Painel Administrativo</p>
            <h1>Olá, {adminName}</h1>
          </div>
          <div className="admin-actions">
            <div className="admin-controls">
              <label className="admin-select-label">
                <span>Período</span>
                <select
                  value={globalPeriodDays}
                  onChange={(e) => setGlobalPeriodDays(Number(e.target.value))}
                  className="admin-select"
                >
                  <option value={1}>Últimas 24h</option>
                  <option value={7}>Últimos 7 dias</option>
                  <option value={30}>Últimos 30 dias</option>
                  <option value={90}>Últimos 90 dias</option>
                </select>
              </label>
              <button
                className="admin-button secondary"
                onClick={refreshAll}
              >
                Atualizar
              </button>
              <label className="toggle toggle-pill">
                <input
                  type="checkbox"
                  checked={autoRefreshAll}
                  onChange={(e) => setAutoRefreshAll(e.target.checked)}
                />
                <span>Auto-refresh (15s)</span>
              </label>
              <button
                className="admin-icon-button"
                type="button"
                aria-label="Configurações de modelos"
                onClick={() => {
                  setShowSettingsModal(true);
                  loadAIModelConfig();
                }}
              >
                <SettingsIcon size={18} />
              </button>
            </div>
            <div className="admin-updated">
              Atualizado: {lastUpdated ? formatDateTime(lastUpdated) : '—'}
            </div>
          </div>
        </header>

        {loading && <div className="admin-loading">Carregando métricas...</div>}
        {error && <div className="admin-error">{error}</div>}

        <div className="admin-tabs">
          <button
            className={`admin-tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <HomeIcon size={16} /> Visão Geral
          </button>
          <button
            className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            <UserIcon size={16} /> Usuários
          </button>
          <button
            className={`admin-tab ${activeTab === 'admins' ? 'active' : ''}`}
            onClick={() => setActiveTab('admins')}
          >
            <ShieldIcon size={16} /> Admins
          </button>
          <button
            className={`admin-tab ${activeTab === 'errors' ? 'active' : ''}`}
            onClick={() => setActiveTab('errors')}
          >
            <AlertCircleIcon size={16} /> Erros
          </button>
          <button
            className={`admin-tab ${activeTab === 'campaigns' ? 'active' : ''}`}
            onClick={() => setActiveTab('campaigns')}
          >
            <TargetIcon size={16} /> Campanhas
          </button>
          <button
            className={`admin-tab ${activeTab === 'performance' ? 'active' : ''}`}
            onClick={() => setActiveTab('performance')}
          >
            <ZapIcon size={16} /> Performance
          </button>
          <button
            className={`admin-tab ${activeTab === 'feedbacks' ? 'active' : ''}`}
            onClick={() => setActiveTab('feedbacks')}
          >
            <ChatIcon size={16} /> Feedbacks
            {feedbacks.filter(f => !f.is_read).length > 0 && (
              <span className="admin-tab-badge">
                {feedbacks.filter(f => !f.is_read).length}
              </span>
            )}
          </button>
          <button
            className={`admin-tab ${activeTab === 'metrics' ? 'active' : ''}`}
            onClick={() => setActiveTab('metrics')}
          >
            <TrendingUpIcon size={16} /> Métricas
          </button>
        </div>

        {stats && activeTab === 'overview' && (
          <>
            <section className="admin-grid">
              <div className="admin-card clickable" onClick={() => setActiveTab('users')}>
                <span className="admin-metric-label">Usuários</span>
                <strong className="admin-metric-value">{stats.total_users}</strong>
                <p className="admin-metric-hint">Ver detalhes de usuários</p>
              </div>
              <div className="admin-card clickable" onClick={() => setActiveTab('performance')}>
                <span className="admin-metric-label">Uploads (7d)</span>
                <strong className="admin-metric-value">{stats.uploads_last_7_days}</strong>
                <p className="admin-metric-hint">Ir para performance</p>
              </div>
              <div className="admin-card clickable" onClick={() => setActiveTab('metrics')}>
                <span className="admin-metric-label">Perguntas IA (7d)</span>
                <strong className="admin-metric-value">{stats.ai_requests_last_7_days}</strong>
                <p className="admin-metric-hint">Abrir métricas</p>
              </div>
              <div className="admin-card">
                <span className="admin-metric-label">Usuários ativos (30d)</span>
                <strong className="admin-metric-value">{stats.active_users_last_30_days}</strong>
              </div>
              <div className="admin-card">
                <span className="admin-metric-label">Taxa de conclusão</span>
                <strong className="admin-metric-value">{stats.completion_rate}%</strong>
              </div>
            </section>

            {stats.language_stats && (
              <section className="admin-card admin-panel">
                <div className="admin-card-header">
                  <h3>🌐 Idiomas dos Usuários</h3>
                </div>
                <div className="admin-grid" style={{ marginTop: '1rem' }}>
                  <div className="admin-card">
                    <span className="admin-metric-label">Usuários PT</span>
                    <strong className="admin-metric-value">{stats.language_stats.users_by_language.pt.toLocaleString('pt-BR')}</strong>
                  </div>
                  <div className="admin-card">
                    <span className="admin-metric-label">Usuários EN</span>
                    <strong className="admin-metric-value">{stats.language_stats.users_by_language.en.toLocaleString('pt-BR')}</strong>
                  </div>
                  <div className="admin-card">
                    <span className="admin-metric-label">Anônimos PT</span>
                    <strong className="admin-metric-value">{stats.language_stats.anonymous_by_language.pt.toLocaleString('pt-BR')}</strong>
                  </div>
                  <div className="admin-card">
                    <span className="admin-metric-label">Anônimos EN</span>
                    <strong className="admin-metric-value">{stats.language_stats.anonymous_by_language.en.toLocaleString('pt-BR')}</strong>
                  </div>
                </div>
              </section>
            )}

            {stats.ai_tracking && (
              <section className="admin-card admin-panel">
                <div className="admin-card-header">
                  <h3>📊 Rastreamento de IA</h3>
                </div>
                <div className="admin-grid" style={{ marginTop: '1rem' }}>
                  <div className="admin-card">
                    <span className="admin-metric-label">Documentos analisados</span>
                    <strong className="admin-metric-value">{stats.ai_tracking.total_documents_analyzed.toLocaleString('pt-BR')}</strong>
                  </div>
                  <div className="admin-card">
                    <span className="admin-metric-label">Analisados (7d)</span>
                    <strong className="admin-metric-value">{stats.ai_tracking.documents_analyzed_last_7_days.toLocaleString('pt-BR')}</strong>
                  </div>
                  <div className="admin-card">
                    <span className="admin-metric-label">Tempo médio de processamento</span>
                    <strong className="admin-metric-value">
                      {safeFixed(stats.ai_tracking.avg_processing_time_seconds, 1)}s
                    </strong>
                  </div>
                  <div className="admin-card">
                    <span className="admin-metric-label">Tempo médio de resposta</span>
                    <strong className="admin-metric-value">
                      {safeFixed(stats.ai_tracking.questions_stats?.avg_response_time_ms, 0)}ms
                    </strong>
                  </div>
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <h4 style={{ marginBottom: '1rem', fontSize: '1rem', color: '#4a5568' }}>Tokens usados por estágio</h4>
                  <div className="admin-grid">
                    <div className="admin-card">
                      <span className="admin-metric-label">Total</span>
                      <strong className="admin-metric-value">{stats.ai_tracking.processing_tokens.total.toLocaleString('pt-BR')}</strong>
                    </div>
                    <div className="admin-card">
                      <span className="admin-metric-label">Verificação</span>
                      <strong className="admin-metric-value">{stats.ai_tracking.processing_tokens.verification.toLocaleString('pt-BR')}</strong>
                    </div>
                    <div className="admin-card">
                      <span className="admin-metric-label">Resumo</span>
                      <strong className="admin-metric-value">{stats.ai_tracking.processing_tokens.summary.toLocaleString('pt-BR')}</strong>
                    </div>
                    <div className="admin-card">
                      <span className="admin-metric-label">Riscos</span>
                      <strong className="admin-metric-value">{stats.ai_tracking.processing_tokens.risk.toLocaleString('pt-BR')}</strong>
                    </div>
                    <div className="admin-card">
                      <span className="admin-metric-label">Recomendações</span>
                      <strong className="admin-metric-value">{stats.ai_tracking.processing_tokens.recommendations.toLocaleString('pt-BR')}</strong>
                    </div>
                    <div className="admin-card">
                      <span className="admin-metric-label">Cláusulas</span>
                      <strong className="admin-metric-value">{stats.ai_tracking.processing_tokens.clauses.toLocaleString('pt-BR')}</strong>
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <h4 style={{ marginBottom: '1rem', fontSize: '1rem', color: '#4a5568' }}>Custos estimados (em centavos)</h4>
                  <div className="admin-grid">
                    <div className="admin-card">
                      <span className="admin-metric-label">Total</span>
                      <strong className="admin-metric-value">¢{stats.ai_tracking.processing_costs_cents.total.toLocaleString('pt-BR')}</strong>
                      <p className="admin-metric-hint">R$ {(stats.ai_tracking.processing_costs_cents.total / 100).toFixed(2)}</p>
                    </div>
                    <div className="admin-card">
                      <span className="admin-metric-label">Processamento</span>
                      <strong className="admin-metric-value">¢{stats.ai_tracking.processing_costs_cents.processing.toLocaleString('pt-BR')}</strong>
                      <p className="admin-metric-hint">R$ {(stats.ai_tracking.processing_costs_cents.processing / 100).toFixed(2)}</p>
                    </div>
                    <div className="admin-card">
                      <span className="admin-metric-label">Perguntas</span>
                      <strong className="admin-metric-value">¢{stats.ai_tracking.processing_costs_cents.questions.toLocaleString('pt-BR')}</strong>
                      <p className="admin-metric-hint">R$ {(stats.ai_tracking.processing_costs_cents.questions / 100).toFixed(2)}</p>
                    </div>
                    <div className="admin-card">
                      <span className="admin-metric-label">Tokens de perguntas</span>
                      <strong className="admin-metric-value">{stats.ai_tracking.questions_stats.total_tokens.toLocaleString('pt-BR')}</strong>
                    </div>
                  </div>
                </div>
              </section>
            )}
          </>
        )}

        {stats && activeTab === 'performance' && (
          <>
            {realtimeDocs ? (
              <section className="admin-card live-processing-card">
                <div className="admin-card-header">
                  <h3>Processando (ao vivo)</h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className="admin-metric-hint">Intervalo:</span>
                    <select
                      value={realtimeIntervalMs}
                      onChange={(e) => setRealtimeIntervalMs(Number(e.target.value))}
                      className="admin-select"
                      style={{ width: '110px' }}
                    >
                      <option value={1000}>1s</option>
                      <option value={3000}>3s</option>
                      <option value={5000}>5s</option>
                      <option value={10000}>10s</option>
                    </select>
                  </div>
                </div>
                <div className="live-processing-grid">
                  <div className="live-processing-item">
                    <span className="admin-metric-label">Processando agora</span>
                    <strong className="admin-metric-value">{realtimeDocs.processing}</strong>
                    <p className="admin-metric-hint">Em fila ou analisando</p>
                  </div>
                  <div className="live-processing-item">
                    <span className="admin-metric-label">Concluídos</span>
                    <strong className="admin-metric-value">{realtimeDocs.completed}</strong>
                    <p className="admin-metric-hint">Snapshot a cada {(realtimeIntervalMs / 1000).toFixed(0)}s</p>
                  </div>
                  <div className="live-processing-item">
                    <span className="admin-metric-label">Falhados</span>
                    <strong className="admin-metric-value">{realtimeDocs.failed}</strong>
                    <p className="admin-metric-hint">Snapshot a cada {(realtimeIntervalMs / 1000).toFixed(0)}s</p>
                  </div>
                  <div className="live-processing-item">
                    <span className="admin-metric-label">Total monitorado</span>
                    <strong className="admin-metric-value">{realtimeDocs.total}</strong>
                    <p className="admin-metric-hint">Último snapshot ({(realtimeIntervalMs / 1000).toFixed(0)}s)</p>
                  </div>
                </div>
              </section>
            ) : (
              <section className="admin-card live-processing-card">
                <div className="admin-card-header">
                  <h3>Processando (ao vivo)</h3>
                </div>
                <div className="admin-empty">Carregando status em tempo real...</div>
              </section>
            )}

            <section className="admin-grid">
              <div className="admin-card">
                <span className="admin-metric-label">Tempo médio (todos)</span>
                <strong className="admin-metric-value">
                  {stats.processing_metrics?.average_processing_seconds?.toFixed(2) ?? '0'}s
                </strong>
                <p className="admin-metric-hint">Baseado em {stats.processing_metrics?.sample_size ?? 0} análises</p>
              </div>
              <div className="admin-card">
                <span className="admin-metric-label">Tempo médio (24h)</span>
                <strong className="admin-metric-value">
                  {stats.processing_metrics?.average_processing_seconds_24h?.toFixed(2) ?? '0'}s
                </strong>
              </div>
              <div className="admin-card">
                <span className="admin-metric-label">Tamanho médio</span>
                <strong className="admin-metric-value">
                  {(stats.processing_metrics?.average_file_size_bytes ?? 0) > 0
                    ? `${Math.round((stats.processing_metrics!.average_file_size_bytes / 1024) * 10) / 10} KB`
                    : '0 KB'}
                </strong>
              </div>
              <div className="admin-card">
                <span className="admin-metric-label">Tamanho médio (24h)</span>
                <strong className="admin-metric-value">
                  {(stats.processing_metrics?.average_file_size_bytes_24h ?? 0) > 0
                    ? `${Math.round((stats.processing_metrics!.average_file_size_bytes_24h / 1024) * 10) / 10} KB`
                    : '0 KB'}
                </strong>
              </div>
              <div className="admin-card">
                <span className="admin-metric-label">Caracteres médios</span>
                <strong className="admin-metric-value">
                  {stats.processing_metrics?.average_char_count?.toFixed(0) ?? '0'}
                </strong>
              </div>
              <div className="admin-card">
                <span className="admin-metric-label">Caracteres médios (24h)</span>
                <strong className="admin-metric-value">
                  {stats.processing_metrics?.average_char_count_24h?.toFixed(0) ?? '0'}
                </strong>
              </div>
            </section>

            <section className="admin-charts">
              {(!stats.processing_metrics ||
                (stats.processing_metrics.processing_time_over_time?.length ?? 0) === 0) && (
                  <div className="admin-empty">Sem dados de performance ainda.</div>
              )}
              {(stats.processing_metrics?.processing_time_over_time?.length ?? 0) > 0 && (
                <AvgLineChart
                  title="Tempo médio de processamento (s) por dia"
                  data={stats.processing_metrics?.processing_time_over_time || []}
                  unit="s"
                />
              )}
              {(stats.processing_metrics?.file_size_over_time?.length ?? 0) > 0 && (
                <AvgLineChart
                  title="Tamanho médio do arquivo (KB) por dia"
                  data={(stats.processing_metrics?.file_size_over_time || []).map((d) => ({
                    ...d,
                    average: Math.round((d.average / 1024) * 10) / 10,
                  }))}
                  unit="KB"
                />
              )}
              {(stats.processing_metrics?.char_count_over_time?.length ?? 0) > 0 && (
                <AvgLineChart
                  title="Caracteres médios por dia"
                  data={stats.processing_metrics?.char_count_over_time || []}
                  unit="chars"
                />
              )}
            </section>

            {(stats.processing_metrics?.processing_table?.length ?? 0) > 0 && (
              <section className="admin-card">
                <div className="admin-card-header">
                  <h3>Documentos processados</h3>
                  <div className="table-limit-controls">
                    <label htmlFor="table-limit">Mostrar:</label>
                    <select
                      id="table-limit"
                      value={processingTableLimit}
                      onChange={(e) => setProcessingTableLimit(Number(e.target.value))}
                      className="table-limit-select"
                    >
                      <option value={100}>100 registros</option>
                      <option value={200}>200 registros</option>
                      <option value={300}>300 registros</option>
                      <option value={500}>500 registros</option>
                      <option value={1000}>1000 registros</option>
                    </select>
                  </div>
                </div>
                <div className="admin-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Data upload</th>
                        <th>Papel</th>
                        <th>Idioma</th>
                        <th>Tempo (s)</th>
                        <th>IA (s)</th>
                        <th>Pré-processo (s)</th>
                        <th>Etapa 1 (s)</th>
                        <th>Etapa 2 (s)</th>
                        <th>QA médio (ms)</th>
                        <th>Símbolos</th>
                        <th>Tamanho</th>
                        <th>Formato</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(stats.processing_metrics?.processing_table || []).map((row, idx) => (
                        <tr key={`${row.document_id}-${idx}`}>
                          <td>{formatDateTime(row.timestamp)}</td>
                          <td>{row.user_role || '-'}</td>
                          <td>{(row.language || 'pt').toUpperCase()}</td>
                          <td>{formatSeconds(row.processing_seconds)}</td>
                          <td>{formatSeconds(row.ai_processing_duration_seconds)}</td>
                          <td>{formatMsToSeconds(row.preprocess_ms)}</td>
                          <td>{formatMsToSeconds(row.stage1_ms)}</td>
                          <td>{formatMsToSeconds(row.stage2_ms)}</td>
                          <td>{formatMs(row.qa_avg_response_time_ms)}</td>
                          <td>{row.char_count}</td>
                          <td>{`${Math.round((row.file_size_bytes / 1024) * 10) / 10} KB`}</td>
                          <td>{row.file_type || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </>
        )}

        {stats && activeTab === 'users' && (
          <>
            <section className="admin-charts">
              <LineChart title="Cadastros ao longo do tempo" data={stats.registrations_over_time} />
              <LineChart title="Uploads de contratos ao longo do tempo" data={stats.uploads_over_time} />
              <LineChart title="Perguntas à IA ao longo do tempo" data={stats.ai_requests_over_time} />
              {stats.anonymous_uploads_over_time && (
                <LineChart title="Uploads anônimos ao longo do tempo" data={stats.anonymous_uploads_over_time} />
              )}
              {stats.unsupported_documents_over_time && (
                <LineChart title="Documentos não suportados ao longo do tempo" data={stats.unsupported_documents_over_time} />
              )}
            </section>

            <section className="admin-card admin-users-list">
              <div className="admin-card-header">
                <h3>Lista de Usuários</h3>
              </div>
              <div className="admin-users-body">
                {users.length === 0 && <div className="admin-empty">Sem usuários ainda.</div>}
                {users.map((user) => {
                  const isExpanded = expandedUserId === user.id;
                  const stats = userStats[user.id];
                  const isLoadingStats = loadingStats[user.id];
                  const isDeleteConfirming = deleteConfirm === user.id;

                  return (
                    <div className="admin-user-container" key={user.id}>
                      <div
                        className="admin-user-row clickable"
                        onClick={(e) => {
                          const target = e.target as HTMLElement;
                          if (target.closest('button') || target.closest('select')) {
                            return;
                          }
                          handleToggleUserExpand(user.id);
                        }}
                      >
                        <div className="admin-user-info">
                          <div className="user-email-row">
                            <div>
                              <strong className="user-email">{user.email}</strong>
                              <div className="user-meta">
                                <span>Criado em {formatDate(user.created_at)}</span>
                              </div>
                            </div>
                            <div className="user-top-right">
                              {user.email_verified ? (
                                <span className="email-verified-badge verified">✓ Verificado</span>
                              ) : (
                                <span className="email-verified-badge unverified">✗ Não verificado</span>
                              )}
                              <div className="user-expand-icon">
                                {isExpanded ? <ChevronUpIcon size={20} /> : <ChevronDownIcon size={20} />}
                              </div>
                            </div>
                          </div>

                          <div className="user-bottom-row">
                            <div className="user-timestamps">
                              <p>Último upload: {formatDateTime(user.last_upload_at)}</p>
                              <p>Última visita: {formatDateTime(user.last_active_at)}</p>
                              <div className="admin-user-tier">
                              {editingUserId === user.id ? (
                                <div className="tier-edit-controls-inline">
                                  <select
                                    value={user.subscription_tier}
                                    onChange={(e) => handleUpdateTier(user.id, e.target.value)}
                                    disabled={updatingTier}
                                    className="tier-select"
                                  >
                                    <option value={SubscriptionTier.FREE}>Gratuito</option>
                                    <option value={SubscriptionTier.PREMIUM}>Premium</option>
                                    <option value={SubscriptionTier.PLATINUM}>Platinum</option>
                                  </select>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingUserId(null);
                                    }}
                                    className="tier-cancel-btn"
                                    disabled={updatingTier}
                                  >
                                    Cancelar
                                  </button>
                                </div>
                              ) : (
                                <div className="tier-display-inline">
                                  <span className={`tier-badge ${getTierBadgeClass(user.subscription_tier)}`}>
                                    {getTierName(user.subscription_tier)}
                                  </span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingUserId(user.id);
                                    }}
                                    className="tier-edit-btn"
                                  >
                                    Alterar Plano
                                  </button>
                                </div>
                              )}
                            </div>
                            </div>
                            
                            <div className="admin-user-metrics">
                              <div>
                                <span>Documentos</span>
                                <strong>{user.document_count}</strong>
                              </div>
                              <div>
                                <span>Perguntas IA</span>
                                <strong>{user.ai_requests}</strong>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="user-expanded-content">
                          {isLoadingStats ? (
                            <div className="loading-stats">Carregando estatísticas...</div>
                          ) : stats ? (
                            <>
                              <div className="user-stats-charts">
                                <div className="user-stat-chart">
                                  <h4>Uploads ao longo do tempo</h4>
                                  {stats.uploads_over_time.length > 0 ? (
                                    <ResponsiveContainer width="100%" height={180}>
                                      <RechartsLineChart data={stats.uploads_over_time} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                                        <CartesianGrid stroke="#e2e8f0" strokeDasharray="4 4" />
                                        <XAxis dataKey="date" tickFormatter={formatShortDate} />
                                        <YAxis allowDecimals={false} />
                                        <Tooltip
                                          formatter={(value) => [`${value ?? 0}`, 'Uploads']}
                                          labelFormatter={(label: string) => `Data: ${formatDate(label)}`}
                                        />
                                        <Line
                                          type="monotone"
                                          dataKey="count"
                                          stroke="#3b82f6"
                                          strokeWidth={2}
                                          dot={{ r: 3, stroke: '#3b82f6', strokeWidth: 1, fill: 'white' }}
                                        />
                                      </RechartsLineChart>
                                    </ResponsiveContainer>
                                  ) : (
                                    <div className="no-data">Sem dados de uploads</div>
                                  )}
                                </div>

                                <div className="user-stat-chart">
                                  <h4>Perguntas à IA ao longo do tempo</h4>
                                  {stats.ai_requests_over_time.length > 0 ? (
                                    <ResponsiveContainer width="100%" height={180}>
                                      <RechartsLineChart data={stats.ai_requests_over_time} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                                        <CartesianGrid stroke="#e2e8f0" strokeDasharray="4 4" />
                                        <XAxis dataKey="date" tickFormatter={formatShortDate} />
                                        <YAxis allowDecimals={false} />
                                        <Tooltip
                                          formatter={(value) => [`${value ?? 0}`, 'Perguntas']}
                                          labelFormatter={(label: string) => `Data: ${formatDate(label)}`}
                                        />
                                        <Line
                                          type="monotone"
                                          dataKey="count"
                                          stroke="#0c8b5c"
                                          strokeWidth={2}
                                          dot={{ r: 3, stroke: '#0c8b5c', strokeWidth: 1, fill: 'white' }}
                                        />
                                      </RechartsLineChart>
                                    </ResponsiveContainer>
                                  ) : (
                                    <div className="no-data">Sem dados de perguntas</div>
                                  )}
                                </div>
                              </div>

                              <div className="user-actions">
                                {isDeleteConfirming ? (
                                  <div className="delete-confirm">
                                    <p>Tem certeza que deseja deletar este usuário? Esta ação não pode ser desfeita.</p>
                                    <div className="delete-confirm-buttons">
                                      <button
                                        className="btn-cancel"
                                        onClick={() => setDeleteConfirm(null)}
                                        disabled={deleting}
                                      >
                                        Cancelar
                                      </button>
                                      <button
                                        className="btn-delete-confirm"
                                        onClick={() => handleDeleteUser(user.id)}
                                        disabled={deleting}
                                      >
                                        {deleting ? 'Deletando...' : 'Confirmar Exclusão'}
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <button
                                    className="btn-delete"
                                    onClick={() => handleDeleteUser(user.id)}
                                  >
                                    <TrashIcon size={18} />
                                    Deletar Usuário
                                  </button>
                                )}
                              </div>
                            </>
                          ) : null}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          </>
        )}

        {stats && activeTab === 'admins' && (
          <>
            <section className="admin-grid">
              <div className="admin-card">
                <span className="admin-metric-label">Admins ativos</span>
                <strong className="admin-metric-value">
                  {stats.admin_users?.filter((a) => a.is_active).length ?? 0}
                </strong>
              </div>
              <div className="admin-card">
                <span className="admin-metric-label">Total de admins</span>
                <strong className="admin-metric-value">{stats.total_admins ?? stats.admin_users?.length ?? 0}</strong>
              </div>
              <div className="admin-card">
                <span className="admin-metric-label">Admins recentes</span>
                <strong className="admin-metric-value">
                  {stats.admin_users?.slice(-3).length ?? 0}
                </strong>
                <p className="admin-metric-hint">Últimas adições</p>
              </div>
            </section>

            <section className="admin-card admin-panel admin-admins">
              <div className="admin-card-header">
                <h3>Gestão de Admins</h3>
                <p className="admin-metric-hint">Visualize quem tem acesso administrativo. (Criação/edição via backend por enquanto.)</p>
              </div>
              {!stats.admin_users || stats.admin_users.length === 0 ? (
                <div className="admin-empty">Nenhum admin cadastrado.</div>
              ) : (
                <div className="admin-admins-list">
                  {stats.admin_users.map((admin) => (
                    <div key={admin.id} className="admin-admin-card">
                      <div className="admin-admin-top">
                        <div>
                          <strong className="admin-admin-name">{admin.username}</strong>
                          <p className="admin-metric-hint">Criado em {formatDate(admin.created_at)}</p>
                        </div>
                        <span className={`admin-admin-badge ${admin.is_active ? 'active' : 'inactive'}`}>
                          {admin.is_active ? 'Ativo' : 'Inativo'}
                        </span>
                      </div>
                      <div className="admin-admin-meta">
                        <span>Último login: {formatDateTime(admin.last_login_at)}</span>
                        <span>ID #{admin.id}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="admin-note">Para criar, editar senha ou remover admins, utilize o fluxo seguro configurado no backend.</div>
            </section>
          </>
        )}

        {activeTab === 'campaigns' && (
          <>
            <section className="admin-card admin-panel">
              <div className="admin-card-header">
                <h3>Campanhas</h3>
                <div className="table-limit-controls">
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginLeft: '12px', fontSize: '14px' }}>
                    <input
                      type="checkbox"
                      checked={showHiddenCampaigns}
                      onChange={(e) => setShowHiddenCampaigns(e.target.checked)}
                    />
                    <span>Mostrar ocultas</span>
                  </label>
                </div>
              </div>
              {loadingCampaigns && <div className="admin-loading">Carregando campanhas...</div>}
              {!loadingCampaigns && (!campaigns || !campaigns.campaigns || campaigns.campaigns.length === 0) && (
                <div className="admin-empty">Sem dados de campanhas ainda. Use URLs com ?source= para iniciar.</div>
              )}
              {!loadingCampaigns && campaigns && campaigns.campaigns && campaigns.campaigns.length > 0 && visibleCampaigns.length === 0 && (
                <div className="admin-empty">Todas as campanhas estão ocultas. Marque "Mostrar ocultas" para visualizá-las.</div>
              )}
              {!loadingCampaigns && campaigns && campaigns.campaigns && visibleCampaigns.length > 0 && (
                <>
                  <div className="admin-grid" style={{ marginBottom: '16px', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
                    <div className="admin-card">
                      <span className="admin-metric-label">Visitantes</span>
                      <strong className="admin-metric-value">{campaignAggregates.totalVisitors.toLocaleString('pt-BR')}</strong>
                    </div>
                    <div className="admin-card">
                      <span className="admin-metric-label">Registros</span>
                      <strong className="admin-metric-value">{campaignAggregates.totalRegistrations.toLocaleString('pt-BR')}</strong>
                      <p className="admin-metric-hint">Conv. Visita→Reg: {campaignAggregates.convVisitToReg.toFixed(1)}%</p>
                    </div>
                    <div className="admin-card">
                      <span className="admin-metric-label">Uploads</span>
                      <strong className="admin-metric-value">{campaignAggregates.totalUploads.toLocaleString('pt-BR')}</strong>
                      <p className="admin-metric-hint">Conv. Reg→Upload: {campaignAggregates.convRegToUpload.toFixed(1)}%</p>
                    </div>
                    <div className="admin-card">
                      <span className="admin-metric-label">Pagos</span>
                      <strong className="admin-metric-value">{campaignAggregates.totalPaid.toLocaleString('pt-BR')}</strong>
                      <p className="admin-metric-hint">Conv. Upload→Pago: {campaignAggregates.convUploadToPaid.toFixed(1)}%</p>
                    </div>
                  </div>

                  <div className="admin-grid" style={{ gridTemplateColumns: '2fr 1fr', gap: '16px', marginBottom: '16px' }}>
                    <div className="admin-card admin-chart">
                      <div className="admin-card-header"><h4>Visitas por dia</h4></div>
                      <div className="admin-chart-body" style={{ height: 260 }}>
                        {campaignVisitsOverTime.length === 0 ? (
                          <div className="admin-empty">Sem dados</div>
                        ) : (
                          <ResponsiveContainer>
                            <RechartsLineChart data={campaignVisitsOverTime}>
                              <CartesianGrid stroke="#e2e8f0" strokeDasharray="4 4" />
                              <XAxis dataKey="date" tickFormatter={formatShortDate} />
                              <YAxis allowDecimals={false} />
                              <Tooltip formatter={(value: number | string | undefined) => [`${value ?? 0}`, 'Visitas']} labelFormatter={(label: string) => formatDate(label)} />
                              <Line type="monotone" dataKey="count" stroke="#0ea5e9" strokeWidth={2.2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                            </RechartsLineChart>
                          </ResponsiveContainer>
                        )}
                      </div>
                    </div>

                    <div className="admin-card admin-chart">
                      <div className="admin-card-header"><h4>Média de visitas por horário (24h)</h4></div>
                      <div className="admin-chart-body" style={{ height: 260 }}>
                        {campaignVisitsByHour.length === 0 ? (
                          <div className="admin-empty">Sem dados</div>
                        ) : (
                          <ResponsiveContainer>
                            <RadialBarChart
                              data={campaignVisitsByHour}
                              innerRadius="35%"
                              outerRadius="80%"
                              startAngle={90}
                              endAngle={-270}
                            >
                              <PolarAxis dataKey="label" tick={{ fontSize: 10 }} />
                              <RadialBar
                                background
                                dataKey="avg"
                                cornerRadius={6}
                                fill="#22c55e"
                              />
                              <Tooltip formatter={(value: number | string | undefined, _name, props: any) => [`${Number(value ?? 0).toFixed(2)} visitas`, props?.payload?.label]} />
                            </RadialBarChart>
                          </ResponsiveContainer>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="admin-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px', marginBottom: '16px' }}>
                    <div className="admin-card admin-chart">
                      <div className="admin-card-header"><h4>Top conv. Visita→Reg</h4></div>
                      <div className="admin-chart-body" style={{ height: 260 }}>
                        {campaignChartsData.conversionBars.length === 0 ? (
                          <div className="admin-empty">Sem dados</div>
                        ) : (
                          <ResponsiveContainer>
                            <BarChart data={campaignChartsData.conversionBars}>
                              <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                              <YAxis tick={{ fontSize: 12 }} domain={[0, 100]} />
                              <Tooltip formatter={(value: number | string | undefined) => [`${Number(value ?? 0).toFixed(1)}%`, 'Conversão']} />
                              <Bar dataKey="visitToReg" fill={PALETTE[0]} name="Visita→Reg" radius={[6, 6, 0, 0]} />
                              <Bar dataKey="regToUpload" fill={PALETTE[1]} name="Reg→Upload" radius={[6, 6, 0, 0]} />
                              <Bar dataKey="uploadToPaid" fill={PALETTE[2]} name="Upload→Pago" radius={[6, 6, 0, 0]} />
                              <Legend />
                            </BarChart>
                          </ResponsiveContainer>
                        )}
                      </div>
                    </div>

                    <div className="admin-card admin-chart">
                      <div className="admin-card-header"><h4>Uploads por visitante</h4></div>
                      <div className="admin-chart-body" style={{ height: 260 }}>
                        {campaignChartsData.uploadsPerVisitor.length === 0 ? (
                          <div className="admin-empty">Sem dados</div>
                        ) : (
                          <ResponsiveContainer>
                            <BarChart data={campaignChartsData.uploadsPerVisitor}>
                              <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                              <YAxis tick={{ fontSize: 12 }} />
                              <Tooltip formatter={(value: number | string | undefined) => [`${Number(value ?? 0).toFixed(2)}`, 'Uploads por visitante']} />
                              <Bar dataKey="uploadsPerVisitor" fill={PALETTE[3]} radius={[6, 6, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        )}
                      </div>
                    </div>

                    <div className="admin-card admin-chart">
                      <div className="admin-card-header"><h4>Uploads anônimos por campanha</h4></div>
                      <div className="admin-chart-body" style={{ height: 260 }}>
                        {campaignChartsData.anonymousUploadsPerCampaign.length === 0 ? (
                          <div className="admin-empty">Sem dados</div>
                        ) : (
                          <ResponsiveContainer>
                            <BarChart data={campaignChartsData.anonymousUploadsPerCampaign}>
                              <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                              <YAxis tick={{ fontSize: 12 }} />
                              <Tooltip formatter={(value: number | string | undefined) => [`${Number(value ?? 0).toLocaleString('pt-BR')}`, 'Uploads anônimos']} />
                              <Bar dataKey="anonymousUploads" fill={PALETTE[4]} radius={[6, 6, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        )}
                      </div>
                    </div>

                    <div className="admin-card admin-chart">
                      <div className="admin-card-header"><h4>Mix de planos pagos</h4></div>
                      <div className="admin-chart-body" style={{ height: 260 }}>
                        {campaignChartsData.paidMix.length === 0 ? (
                          <div className="admin-empty">Sem dados</div>
                        ) : (
                          <ResponsiveContainer>
                            <BarChart data={campaignChartsData.paidMix} stackOffset="expand">
                              <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                              <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${(v * 100).toFixed(0)}%`} />
                              <Tooltip formatter={(value: number | string | undefined) => [`${(Number(value ?? 0) * 100).toFixed(1)}%`, 'Participação']} />
                              <Bar dataKey="premium" stackId="paid" fill={PALETTE[4]} name="Premium" />
                              <Bar dataKey="platinum" stackId="paid" fill={PALETTE[5]} name="Platinum" />
                              <Legend />
                            </BarChart>
                          </ResponsiveContainer>
                        )}
                      </div>
                    </div>

                    <div className="admin-card admin-chart">
                      <div className="admin-card-header"><h4>Participação de tráfego</h4></div>
                      <div className="admin-chart-body" style={{ height: 260 }}>
                        {campaignChartsData.trafficShare.length === 0 ? (
                          <div className="admin-empty">Sem dados</div>
                        ) : (
                          <ResponsiveContainer>
                            <PieChart>
                              <Pie data={campaignChartsData.trafficShare} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100} paddingAngle={3}>
                                {campaignChartsData.trafficShare.map((entry, idx) => (
                                  <Cell key={entry.name} fill={PALETTE[idx % PALETTE.length]} />
                                ))}
                              </Pie>
                              <Tooltip formatter={(value: number | string | undefined) => [`${Number(value ?? 0).toLocaleString('pt-BR')}`, 'Visitantes']} />
                              <Legend />
                            </PieChart>
                          </ResponsiveContainer>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="admin-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Campanha</th>
                          <th>Visitantes</th>
                        <th>Logins</th>
                        <th>Registros</th>
                        <th>PT</th>
                        <th>EN</th>
                        <th>Anon.</th>
                        <th>Uploads</th>
                        <th>Uploads anôn.</th>
                        <th>Premium</th>
                        <th>Platinum</th>
                        <th>Conv. Visita→Reg</th>
                        <th>Conv. Reg→Upload</th>
                          <th>Conv. Upload→Pago</th>
                          <th>Funil Upload</th>
                          <th>Tempo Médio</th>
                          <th>Primeiro / Último</th>
                          <th>Landing Pages</th>
                          <th>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                      {(() => {
                        const visible = visibleCampaigns
                          .slice()
                          .sort((a, b) => safeTimestamp(b.last_activity) - safeTimestamp(a.last_activity));

                        if (visible.length === 0) {
                          return (
                            <tr>
                              <td colSpan={19} style={{ textAlign: 'center', padding: '16px' }}>
                                Todas as campanhas estão ocultas para este período. Marque "Mostrar ocultas" para vê-las.
                              </td>
                            </tr>
                          );
                        }

                        return visible.map((c) => {
                              const srcKey = normalizeSource(c.source);
                              const isHidden = hiddenCampaignSet.has(srcKey) || c.is_hidden;
                              return (
                            <tr key={c.source}>
                              <td><strong>{displaySource(c.source)}</strong></td>
                              <td>{c.visitors}</td>
                              <td>{c.logins}</td>
                              <td>{c.registered_users}</td>
                              <td>{c.users_pt || 0}</td>
                              <td>{c.users_en || 0}</td>
                              <td>{c.anonymous_users}</td>
                              <td>{c.registered_uploads}</td>
                              <td>{c.anonymous_uploads}</td>
                              <td>{c.paid_premium}</td>
                              <td>{c.paid_platinum}</td>
                              <td>{c.visitor_to_registration}%</td>
                              <td>{c.registration_to_upload}%</td>
                              <td>{c.upload_to_paid}%</td>
                              <td>
                                {(c.funnel_started ?? 0) > 0 ? (
                                  <div className="admin-metric-hint" style={{ fontSize: '11px', lineHeight: '1.3' }}>
                                    <div>Ini: {c.funnel_started ?? 0}</div>
                                    <div>Role: {c.funnel_role_selected ?? 0} <span style={{ color: '#ef4444' }}>(-{c.funnel_role_dropoff_pct ?? 0}%)</span></div>
                                    <div>File: {c.funnel_file_selected ?? 0} <span style={{ color: '#ef4444' }}>(-{c.funnel_file_dropoff_pct ?? 0}%)</span></div>
                                    <div style={{ color: '#22c55e' }}>OK: {c.funnel_upload_completed ?? 0}</div>
                                  </div>
                                ) : '—'}
                              </td>
                              <td>
                                {c.avg_session_seconds && c.avg_session_seconds > 0
                                  ? `${Math.floor(c.avg_session_seconds / 60)}m ${c.avg_session_seconds % 60}s`
                                  : '—'}
                              </td>
                              <td>
                                <div className="admin-metric-hint">
                                  {c.first_seen ? formatDate(c.first_seen) : '—'} / {c.last_activity ? formatDate(c.last_activity) : '—'}
                                </div>
                              </td>
                              <td>{(c.top_landing_pages || []).join(', ') || '—'}</td>
                              <td>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                                  {isHidden && <span className="admin-badge muted">Oculta</span>}
                                  {isHidden ? (
                                    <button className="admin-button secondary small" onClick={() => handleUnhideCampaign(c.source)}>
                                      Reexibir
                                    </button>
                                  ) : (
                                    <button className="admin-button danger small" onClick={() => handleHideCampaign(c.source)}>
                                      Ocultar
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
                </>
              )}
            </section>
          </>
        )}

        {activeTab === 'feedbacks' && (
          <>
            <section className="admin-card admin-panel">
              <div className="admin-card-header">
                <h3>E-mails para Notificações</h3>
              </div>
              <div className="admin-card-body">
                <div className="feedback-email-form">
                  <input
                    type="email"
                    placeholder="adicionar@email.com"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    className="feedback-email-input"
                  />
                  <button
                    onClick={handleAddEmail}
                    disabled={addingEmail || !newEmail.trim()}
                    className="admin-button"
                  >
                    {addingEmail ? 'Adicionando...' : 'Adicionar E-mail'}
                  </button>
                </div>

                {emailConfigs.length === 0 ? (
                  <div className="admin-empty">Nenhum e-mail configurado ainda.</div>
                ) : (
                  <div className="feedback-emails-list">
                    {emailConfigs.map((config) => (
                      <div key={config.id} className="feedback-email-item">
                        <div className="email-info">
                          <strong>{config.email}</strong>
                          <span className={`email-status ${config.is_active ? 'active' : 'inactive'}`}>
                            {config.is_active ? 'Ativo' : 'Inativo'}
                          </span>
                        </div>
                        <div className="email-actions">
                          <button
                            onClick={() => handleToggleEmailActive(config.id, !config.is_active)}
                            className="admin-button secondary"
                          >
                            {config.is_active ? 'Desativar' : 'Ativar'}
                          </button>
                          <button
                            onClick={() => handleDeleteEmail(config.id)}
                            className="admin-button danger"
                          >
                            Remover
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>

            <section className="admin-card admin-panel">
              <div className="admin-card-header">
                <h3>Feedbacks Recebidos</h3>
                <label className="feedback-filter">
                  <input
                    type="checkbox"
                    checked={showUnreadOnly}
                    onChange={(e) => setShowUnreadOnly(e.target.checked)}
                  />
                  <span>Apenas não lidos</span>
                </label>
              </div>
              <div className="admin-card-body">
                {loadingFeedbacks ? (
                  <div className="admin-loading">Carregando feedbacks...</div>
                ) : feedbacks.length === 0 ? (
                  <div className="admin-empty">
                    {showUnreadOnly ? 'Nenhum feedback não lido.' : 'Nenhum feedback ainda.'}
                  </div>
                ) : (
                  <div className="feedbacks-list">
                    {feedbacks.map((feedback) => (
                      <div
                        key={feedback.id}
                        className={`feedback-item ${feedback.is_read ? 'read' : 'unread'}`}
                      >
                        <div className="feedback-header">
                          <div className="feedback-user">
                            {feedback.user_name ? (
                              <strong>{feedback.user_name}</strong>
                            ) : feedback.email ? (
                              <strong>{feedback.email}</strong>
                            ) : (
                              <strong>Anônimo</strong>
                            )}
                            <span className="feedback-date">
                              {formatDate(feedback.created_at)}
                            </span>
                          </div>
                          <div className="feedback-actions">
                            {!feedback.is_read && (
                              <button
                                onClick={() => handleMarkAsRead(feedback.id)}
                                className="admin-button secondary small"
                              >
                                Marcar como lido
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteFeedback(feedback.id)}
                              className="admin-button danger small"
                            >
                              Deletar
                            </button>
                          </div>
                        </div>
                        <div className="feedback-message">
                          {feedback.message}
                        </div>
                        {((feedback.attachments && feedback.attachments.length > 0) || feedback.attachment_filename || feedback.has_attachment) && (
                          <div className="feedback-attachment">
                            <div className="feedback-attachment-meta">
                              <strong>Anexos</strong>
                              <span>{feedback.attachments?.length ? `${feedback.attachments.length} arquivo(s)` : '1 arquivo'}</span>
                            </div>
                            <div className="feedback-attachment-actions">
                              {(feedback.attachments && feedback.attachments.length > 0) ? (
                                feedback.attachments.map((att) => (
                                  <button
                                    key={att.id}
                                    onClick={() => handleViewFeedbackAttachment(feedback, att.id)}
                                    className="admin-button secondary small"
                                  >
                                    Ver
                                    {att.attachment_filename ? `: ${att.attachment_filename}` : ''}
                                    {att.attachment_size ? ` (${formatBytes(att.attachment_size)})` : ''}
                                  </button>
                                ))
                              ) : (
                                <button
                                  onClick={() => handleViewFeedbackAttachment(feedback)}
                                  className="admin-button secondary small"
                                >
                                  Ver anexo
                                  {feedback.attachment_size ? ` (${formatBytes(feedback.attachment_size)})` : ''}
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>
          </>
        )}

        {activeTab === 'metrics' && (
          <>
            <section className="admin-card admin-panel">
              <div className="admin-card-header">
                <h3>Métricas e Análise de Produto</h3>
              </div>

              {loadingMetrics ? (
                <div className="admin-loading">Carregando métricas...</div>
              ) : metricsDashboard ? (
                <>
                  {renderMetricsSection('users', '📊 Usuários', (
                    <div className="admin-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
                      <div className="admin-card">
                        <span className="admin-metric-label">Total (todos)</span>
                        <strong className="admin-metric-value">{metricsDashboard.users.total_all_time.toLocaleString('pt-BR')}</strong>
                        <small style={{ display: 'block', marginTop: '4px', opacity: 0.7 }}>Desde o início</small>
                      </div>
                      <div className="admin-card">
                        <span className="admin-metric-label">Novos ({globalPeriodDays}d)</span>
                        <strong className="admin-metric-value">{metricsDashboard.users.new_this_period.toLocaleString('pt-BR')}</strong>
                        <small style={{ display: 'block', marginTop: '4px', opacity: 0.7 }}>Registros recentes</small>
                      </div>
                      <div className="admin-card">
                        <span className="admin-metric-label">Ativos ({globalPeriodDays}d)</span>
                        <strong className="admin-metric-value">{metricsDashboard.users.active_this_period.toLocaleString('pt-BR')}</strong>
                        <small style={{ display: 'block', marginTop: '4px', opacity: 0.7 }}>Com atividade</small>
                      </div>
                      <div className="admin-card">
                        <span className="admin-metric-label">Pagos</span>
                        <strong className="admin-metric-value">{metricsDashboard.users.paid.toLocaleString('pt-BR')}</strong>
                        <small style={{ display: 'block', marginTop: '4px', opacity: 0.7 }}>Premium/Platinum</small>
                      </div>
                    </div>
                  ))}

                  {/* Language Statistics */}
                  {metricsDashboard.language && renderMetricsSection('language', '🌐 Idiomas', (
                    <>
                      <div className="admin-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
                        <div className="admin-card">
                          <span className="admin-metric-label">Usuários PT</span>
                          <strong className="admin-metric-value">{metricsDashboard.language.users_by_language.pt.toLocaleString('pt-BR')}</strong>
                          <small style={{ display: 'block', marginTop: '4px', opacity: 0.7 }}>
                            {safePercentage(metricsDashboard.language.users_by_language.pt, metricsDashboard.language.users_by_language.pt + metricsDashboard.language.users_by_language.en).toFixed(1)}% dos usuários
                          </small>
                        </div>
                        <div className="admin-card">
                          <span className="admin-metric-label">Usuários EN</span>
                          <strong className="admin-metric-value">{metricsDashboard.language.users_by_language.en.toLocaleString('pt-BR')}</strong>
                          <small style={{ display: 'block', marginTop: '4px', opacity: 0.7 }}>
                            {safePercentage(metricsDashboard.language.users_by_language.en, metricsDashboard.language.users_by_language.pt + metricsDashboard.language.users_by_language.en).toFixed(1)}% dos usuários
                          </small>
                        </div>
                        <div className="admin-card">
                          <span className="admin-metric-label">Anônimos PT</span>
                          <strong className="admin-metric-value">{metricsDashboard.language.anonymous_by_language.pt.toLocaleString('pt-BR')}</strong>
                          <small style={{ display: 'block', marginTop: '4px', opacity: 0.7 }}>Sessões anônimas</small>
                        </div>
                        <div className="admin-card">
                          <span className="admin-metric-label">Anônimos EN</span>
                          <strong className="admin-metric-value">{metricsDashboard.language.anonymous_by_language.en.toLocaleString('pt-BR')}</strong>
                          <small style={{ display: 'block', marginTop: '4px', opacity: 0.7 }}>Sessões anônimas</small>
                        </div>
                      </div>
                      <div className="admin-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)', marginTop: '12px' }}>
                        <div className="admin-card">
                          <span className="admin-metric-label">Total PT (usuários + anônimos)</span>
                          <strong className="admin-metric-value">{metricsDashboard.language.total_pt.toLocaleString('pt-BR')}</strong>
                        </div>
                        <div className="admin-card">
                          <span className="admin-metric-label">Total EN (usuários + anônimos)</span>
                          <strong className="admin-metric-value">{metricsDashboard.language.total_en.toLocaleString('pt-BR')}</strong>
                        </div>
                      </div>
                      <div className="admin-charts" style={{ marginTop: '12px', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                        <div className="admin-card admin-chart">
                          <div className="admin-card-header"><h4>Usuários por idioma</h4></div>
                          <div className="admin-chart-body">
                            {!(metricsDashboard.language.users_over_time?.length) ? (
                              <div className="admin-empty">Sem dados</div>
                            ) : (
                              <ResponsiveContainer width="100%" height={240}>
                                <RechartsLineChart data={metricsDashboard.language.users_over_time}>
                                  <CartesianGrid stroke="#e2e8f0" strokeDasharray="4 4" />
                                  <XAxis dataKey="date" tickFormatter={formatShortDate} />
                                  <YAxis allowDecimals={false} />
                                  <Tooltip formatter={(v, n) => [`${v ?? 0}`, n]} labelFormatter={(l: string) => formatDate(l)} />
                                  <Line type="monotone" dataKey="pt" name="PT" stroke="#0ea5e9" strokeWidth={2.2} dot={{ r: 3 }} />
                                  <Line type="monotone" dataKey="en" name="EN" stroke="#8b5cf6" strokeWidth={2.2} dot={{ r: 3 }} />
                                </RechartsLineChart>
                              </ResponsiveContainer>
                            )}
                          </div>
                        </div>
                        <div className="admin-card admin-chart">
                          <div className="admin-card-header"><h4>Anônimos por idioma</h4></div>
                          <div className="admin-chart-body">
                            {!(metricsDashboard.language.anonymous_over_time?.length) ? (
                              <div className="admin-empty">Sem dados</div>
                            ) : (
                              <ResponsiveContainer width="100%" height={240}>
                                <RechartsLineChart data={metricsDashboard.language.anonymous_over_time}>
                                  <CartesianGrid stroke="#e2e8f0" strokeDasharray="4 4" />
                                  <XAxis dataKey="date" tickFormatter={formatShortDate} />
                                  <YAxis allowDecimals={false} />
                                  <Tooltip formatter={(v, n) => [`${v ?? 0}`, n]} labelFormatter={(l: string) => formatDate(l)} />
                                  <Line type="monotone" dataKey="pt" name="PT" stroke="#22c55e" strokeWidth={2.2} dot={{ r: 3 }} />
                                  <Line type="monotone" dataKey="en" name="EN" stroke="#f59e0b" strokeWidth={2.2} dot={{ r: 3 }} />
                                </RechartsLineChart>
                              </ResponsiveContainer>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  ))}

                  {/* Registration Breakdown */}
                  {renderMetricsSection('registration', `📝 Cadastros (${globalPeriodDays} dias)`, (
                    <>
                    <div className="admin-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)', marginBottom: '16px' }}>
                      <div className="admin-card">
                        <span className="admin-metric-label">Via Google</span>
                        <strong className="admin-metric-value">{metricsDashboard.registration.by_method.google}</strong>
                        <small style={{ display: 'block', marginTop: '4px', opacity: 0.7 }}>
                          {metricsDashboard.registration.by_method.google_pct.toFixed(1)}% dos cadastros
                        </small>
                      </div>
                      <div className="admin-card">
                        <span className="admin-metric-label">Via Email</span>
                        <strong className="admin-metric-value">{metricsDashboard.registration.by_method.email}</strong>
                        <small style={{ display: 'block', marginTop: '4px', opacity: 0.7 }}>
                          {metricsDashboard.registration.by_method.email_pct.toFixed(1)}% dos cadastros
                        </small>
                      </div>
                    </div>
                    <div className="admin-card" style={{ padding: '16px' }}>
                      <strong style={{ display: 'block', marginBottom: '12px' }}>Fontes de Registro:</strong>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px' }}>
                        {Object.entries(metricsDashboard.registration.by_source).map(([source, count]) => (
                          <div key={source} style={{ fontSize: '14px', padding: '8px', background: '#f1f5f9', borderRadius: '6px' }}>
                            <strong>{source}:</strong> {count} ({safePercentage(count, metricsDashboard.registration.total).toFixed(1)}%)
                          </div>
                        ))}
                        <div style={{ fontSize: '14px', padding: '8px', background: '#f1f5f9', borderRadius: '6px' }}>
                          <strong>Upload Anônimo:</strong> {metricsDashboard.registration.from_anonymous} ({metricsDashboard.registration.from_anonymous_pct.toFixed(1)}%)
                        </div>
                      </div>
                    </div>
                    </>
                  ))}

                  {/* Traffic & Geo */}
                  {renderMetricsSection('traffic', `🌍 Tráfego, Fonte e Dispositivo (${globalPeriodDays} dias)`, (
                    <>
                    <div className="admin-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                      <div className="admin-card">
                        <span className="admin-metric-label">Visitas Totais</span>
                        <strong className="admin-metric-value">{metricsDashboard.traffic.total}</strong>
                        <small style={{ display: 'block', marginTop: '4px', opacity: 0.7 }}>
                          Eventos page_visit (landing e afins)
                        </small>
                      </div>
                      <div className="admin-card">
                        <span className="admin-metric-label">Páginas</span>
                        <strong className="admin-metric-value">{Object.keys(metricsDashboard.traffic.by_page || {}).length}</strong>
                        <small style={{ display: 'block', marginTop: '4px', opacity: 0.7 }}>Contagem distinta</small>
                      </div>
                      <div className="admin-card">
                        <span className="admin-metric-label">Top Dispositivo</span>
                        <strong className="admin-metric-value">
                          {Object.entries(metricsDashboard.traffic.by_device || {}).sort((a, b) => b[1] - a[1])[0]?.[0] || '—'}
                        </strong>
                        <small style={{ display: 'block', marginTop: '4px', opacity: 0.7 }}>Classe de device mais usada</small>
                      </div>
                    </div>
                    <div className="admin-card" style={{ marginTop: '16px', padding: '16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>📊 Média de visitas por usuário (por página)</h4>
                        <span style={{ fontSize: '12px', color: '#475569' }}>Usuários autenticados • {metricsDashboard.traffic.window_days} dias</span>
                      </div>
                      {avgPageVisitsData.length === 0 ? (
                        <div className="admin-empty">Sem dados para o período.</div>
                      ) : (
                        <div style={{ width: '100%', height: 260 }}>
                          <ResponsiveContainer>
                            <BarChart data={avgPageVisitsData} margin={{ top: 10, right: 10, left: -16, bottom: 10 }}>
                              <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                              <XAxis
                                dataKey="page"
                                tick={{ fontSize: 11 }}
                                interval={0}
                                angle={-20}
                                height={60}
                              />
                              <YAxis allowDecimals tick={{ fontSize: 12 }} />
                              <Tooltip
                                formatter={(value: number | string | undefined) => [
                                  `${Number(value ?? 0).toFixed(2)}`,
                                  'Média por usuário',
                                ]}
                              />
                              <Bar dataKey="avg" fill="#0ea5e9" radius={[6, 6, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      )}
                    </div>
                    <div className="admin-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginTop: '16px' }}>
                      {renderTopList('Fontes (utm/source)', metricsDashboard.traffic.by_source, 'Sem fontes')}
                      {renderTopList('Páginas', metricsDashboard.traffic.by_page, 'Sem páginas')}
                      {renderTopList('Browsers', metricsDashboard.traffic.by_browser, 'Sem browsers')}
                      {renderTopList('Sistemas Operacionais', metricsDashboard.traffic.by_os, 'Sem SO')}
                      {renderTopList('Dispositivos', metricsDashboard.traffic.by_device, 'Sem devices')}
                      {renderTopList('País', metricsDashboard.traffic.by_country, 'Sem países')}
                      {renderTopList('Estado/Região', metricsDashboard.traffic.by_region, 'Sem regiões')}
                      {renderTopList('Cidade', metricsDashboard.traffic.by_city, 'Sem cidades')}
                    </div>
                    </>
                  ))}

                  {/* Screen Time */}
                  {metricsDashboard.screen_time && renderMetricsSection('screen_time', `⏱ Tempo de Tela (${metricsDashboard.screen_time.window_days ?? globalPeriodDays} dias)`, (
                    <>
                      <div className="admin-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                        <div className="admin-card">
                          <span className="admin-metric-label">Média geral</span>
                          <strong className="admin-metric-value">{formatSeconds(metricsDashboard.screen_time.overall_avg_seconds)}</strong>
                          <small style={{ display: 'block', marginTop: '4px', opacity: 0.7 }}>Base: {metricsDashboard.screen_time.samples} saídas</small>
                        </div>
                        <div className="admin-card">
                          <span className="admin-metric-label">Mediana</span>
                          <strong className="admin-metric-value">{formatSeconds(metricsDashboard.screen_time.median_seconds || 0)}</strong>
                          <small style={{ display: 'block', marginTop: '4px', opacity: 0.7 }}>Menos sensível a outliers</small>
                        </div>
                        <div className="admin-card">
                          <span className="admin-metric-label">Página mais engajante</span>
                          <strong className="admin-metric-value">
                            {Object.entries(metricsDashboard.screen_time.by_page || {})
                              .sort((a, b) => (b[1] || 0) - (a[1] || 0))[0]?.[0] || '—'}
                          </strong>
                          <small style={{ display: 'block', marginTop: '4px', opacity: 0.7 }}>Maior tempo médio</small>
                        </div>
                      </div>

                      <div className="admin-charts" style={{ marginTop: '12px', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
                        <div className="admin-card admin-chart">
                          <div className="admin-card-header"><h4>Tempo médio por página</h4></div>
                          <div className="admin-chart-body" style={{ height: 260 }}>
                            {Object.keys(metricsDashboard.screen_time.by_page || {}).length === 0 ? (
                              <div className="admin-empty">Sem dados</div>
                            ) : (
                              <ResponsiveContainer>
                                <BarChart
                                  data={Object.entries(metricsDashboard.screen_time.by_page || {})
                                    .map(([page, secs]) => ({ page, secs }))
                                    .sort((a, b) => b.secs - a.secs)
                                    .slice(0, 10)}
                                  margin={{ top: 10, right: 10, left: -10, bottom: 20 }}
                                >
                                  <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                                  <XAxis dataKey="page" tick={{ fontSize: 11 }} angle={-20} height={60} />
                                  <YAxis tick={{ fontSize: 12 }} />
                                  <Tooltip formatter={(value: number | string | undefined) => [formatSeconds(Number(value ?? 0)), 'Tempo médio']} />
                                  <Bar dataKey="secs" fill="#6366f1" radius={[6, 6, 0, 0]} />
                                </BarChart>
                              </ResponsiveContainer>
                            )}
                          </div>
                        </div>

                        <div className="admin-card admin-chart">
                          <div className="admin-card-header"><h4>Tempo médio por seção</h4></div>
                          <div className="admin-chart-body" style={{ height: 260 }}>
                            {Object.keys(metricsDashboard.screen_time.by_section || {}).length === 0 ? (
                              <div className="admin-empty">Sem dados</div>
                            ) : (
                              <ResponsiveContainer>
                                <BarChart
                                  data={Object.entries(metricsDashboard.screen_time.by_section || {})
                                    .map(([section, secs]) => ({ section, secs }))
                                    .sort((a, b) => b.secs - a.secs)
                                    .slice(0, 10)}
                                  margin={{ top: 10, right: 10, left: -10, bottom: 20 }}
                                >
                                  <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                                  <XAxis dataKey="section" tick={{ fontSize: 11 }} angle={-20} height={60} />
                                  <YAxis tick={{ fontSize: 12 }} />
                                  <Tooltip formatter={(value: number | string | undefined) => [formatSeconds(Number(value ?? 0)), 'Tempo médio']} />
                                  <Bar dataKey="secs" fill="#22c55e" radius={[6, 6, 0, 0]} />
                                </BarChart>
                              </ResponsiveContainer>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  ))}

                  {/* Post-Upload Engagement Funnel */}
                  {renderMetricsSection('post_upload', '🎯 Funil Pós-Upload', (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid #f1f5f9' }}>
                        <div style={{ flex: '0 0 180px' }} />
                        <div style={{ flex: '1' }} />
                        <div style={{ flex: '0 0 44px', textAlign: 'right', fontSize: '11px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total</div>
                        <div style={{ flex: '0 0 80px', textAlign: 'right', fontSize: '11px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>% do base</div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {[
                          { label: 'Enviou Contrato', value: metricsDashboard.post_upload_funnel.uploaded, rate: 100 },
                          { label: 'Viu Riscos', value: metricsDashboard.post_upload_funnel.viewed_risks, rate: metricsDashboard.post_upload_funnel.conversion_rates.upload_to_risks },
                          { label: 'Fez Pergunta', value: metricsDashboard.post_upload_funnel.asked_questions, rate: metricsDashboard.post_upload_funnel.conversion_rates.upload_to_questions },
                          { label: 'Ativou Notificações', value: metricsDashboard.post_upload_funnel.enabled_notifications, rate: metricsDashboard.post_upload_funnel.conversion_rates.upload_to_notifications },
                        ].map((stage, idx) => {
                          const colors = ['#3b82f6', '#6366f1', '#8b5cf6', '#10b981'];
                          return (
                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <div style={{ flex: '0 0 180px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>{stage.label}</div>
                              <div style={{ flex: '1', background: '#f1f5f9', borderRadius: '6px', height: '22px', position: 'relative', overflow: 'hidden' }}>
                                <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${stage.rate}%`, background: colors[idx], opacity: 0.85, transition: 'width 0.4s ease', borderRadius: '6px' }} />
                              </div>
                              <div style={{ flex: '0 0 44px', textAlign: 'right', fontSize: '15px', fontWeight: '700', color: '#111827' }}>{stage.value}</div>
                              <div style={{ flex: '0 0 80px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: idx === 0 ? '#94a3b8' : stage.rate >= 50 ? '#10b981' : stage.rate >= 25 ? '#d97706' : '#dc2626' }}>
                                {idx === 0 ? '100%' : `${stage.rate.toFixed(1)}%`}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ))}

                  {/* Limit Encounter Funnel */}
                  {renderMetricsSection('limit', '💰 Conversão por Limite', (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid #f1f5f9' }}>
                        <div style={{ flex: '0 0 180px' }} />
                        <div style={{ flex: '1' }} />
                        <div style={{ flex: '0 0 44px', textAlign: 'right', fontSize: '11px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total</div>
                        <div style={{ flex: '0 0 80px', textAlign: 'right', fontSize: '11px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>% do base</div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {[
                          { label: 'Atingiu Limite', value: metricsDashboard.limit_encounter.hit_limit, rate: 100 },
                          { label: 'Viu Paywall', value: metricsDashboard.limit_encounter.saw_paywall, rate: metricsDashboard.limit_encounter.conversion_rates.limit_to_paywall },
                          { label: 'Clicou Preços', value: metricsDashboard.limit_encounter.clicked_pricing, rate: metricsDashboard.limit_encounter.conversion_rates.limit_to_pricing },
                          { label: 'Pagou', value: metricsDashboard.limit_encounter.completed_payment, rate: metricsDashboard.limit_encounter.conversion_rates.limit_to_payment },
                        ].map((stage, idx) => {
                          const colors = ['#f59e0b', '#0891b2', '#6366f1', '#10b981'];
                          return (
                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <div style={{ flex: '0 0 180px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>{stage.label}</div>
                              <div style={{ flex: '1', background: '#f1f5f9', borderRadius: '6px', height: '22px', position: 'relative', overflow: 'hidden' }}>
                                <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${stage.rate}%`, background: colors[idx], opacity: 0.85, transition: 'width 0.4s ease', borderRadius: '6px' }} />
                              </div>
                              <div style={{ flex: '0 0 44px', textAlign: 'right', fontSize: '15px', fontWeight: '700', color: '#111827' }}>{stage.value}</div>
                              <div style={{ flex: '0 0 80px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: idx === 0 ? '#94a3b8' : stage.rate >= 50 ? '#10b981' : stage.rate >= 20 ? '#d97706' : '#dc2626' }}>
                                {idx === 0 ? '100%' : `${stage.rate.toFixed(1)}%`}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ))}

                  {/* User Behavior */}
                  <div className="admin-card-body" style={{ marginBottom: '24px' }}>
                    <h4 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}>Comportamento de Usuarios</h4>
                    <div className="admin-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
                      <div className="admin-card">
                        <span className="admin-metric-label">Cadastros (Periodo)</span>
                        <strong className="admin-metric-value">{metricsDashboard.behavior.signups.total}</strong>
                        <small style={{ display: 'block', marginTop: '4px', opacity: 0.7 }}>Base para conversao</small>
                      </div>
                      <div className="admin-card">
                        <span className="admin-metric-label">Cadastros do Landing</span>
                        <strong className="admin-metric-value">{metricsDashboard.behavior.signups.landing}</strong>
                        <small style={{ display: 'block', marginTop: '4px', opacity: 0.7 }}>
                          {metricsDashboard.behavior.signups.landing_pct.toFixed(1)}% dos cadastros
                        </small>
                      </div>
                      <div className="admin-card">
                        <span className="admin-metric-label">Cadastros via Google</span>
                        <strong className="admin-metric-value">{metricsDashboard.behavior.signups.google}</strong>
                        <small style={{ display: 'block', marginTop: '4px', opacity: 0.7 }}>
                          {metricsDashboard.behavior.signups.google_pct.toFixed(1)}% dos cadastros
                        </small>
                      </div>
                      <div className="admin-card">
                        <span className="admin-metric-label">Cadastros apos Anonimo</span>
                        <strong className="admin-metric-value">{metricsDashboard.behavior.signups.from_anonymous}</strong>
                        <small style={{ display: 'block', marginTop: '4px', opacity: 0.7 }}>
                          {metricsDashboard.behavior.signups.anonymous_pct.toFixed(1)}% dos cadastros
                        </small>
                      </div>
                      <div className="admin-card">
                        <span className="admin-metric-label">Cliques em CTAs</span>
                        <strong className="admin-metric-value">{metricsDashboard.behavior.cta_clicks.total}</strong>
                      </div>
                    </div>

                    {/* CTA Clicks Bar Chart */}
                    <div style={{ marginTop: '24px' }}>
                      <h5 style={{ marginBottom: '12px', fontSize: '14px', fontWeight: '600', color: '#64748b' }}>Cliques por Botão</h5>
                      <div style={{ width: '100%', height: 280 }}>
                        {Object.keys(metricsDashboard.behavior.cta_clicks.by_location || {}).length === 0 ? (
                          <div className="admin-empty">Sem dados de cliques</div>
                        ) : (
                          <ResponsiveContainer>
                            <BarChart
                              data={Object.entries(metricsDashboard.behavior.cta_clicks.by_location || {}).map(([name, clicks]) => {
                                const cleanName = name.replace(/"/g, '');
                                return {
                                  name: cleanName === 'hero' ? 'Hero (Criar Conta)' :
                                        cleanName === 'hero_analyze_free' ? 'Hero (Analisar)' :
                                        cleanName === 'select_contract' ? 'Selecionar Contrato' :
                                        cleanName === 'analyze_free' ? 'Selecionar Contrato' :
                                        cleanName === 'feature_risks' ? 'Feature (Riscos)' :
                                        cleanName === 'feature_questions' ? 'Feature (Perguntas)' :
                                        cleanName === 'feature_message' ? 'Feature (Mensagem)' :
                                        cleanName === 'way_beyond_contracts' ? 'Ver Contratos' :
                                        cleanName === 'way_beyond_questions' ? 'Fazer Pergunta' :
                                        cleanName === 'faq_analyze_free' ? 'FAQ (Analisar)' :
                                        cleanName === 'footer' ? 'Footer CTA' : cleanName,
                                  clicks: Number(clicks),
                                };
                              }).sort((a, b) => b.clicks - a.clicks)}
                              margin={{ top: 10, right: 10, left: -10, bottom: 60 }}
                            >
                              <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                              <XAxis
                                dataKey="name"
                                tick={{ fontSize: 11 }}
                                angle={-35}
                                textAnchor="end"
                                interval={0}
                              />
                              <YAxis tick={{ fontSize: 12 }} />
                              <Tooltip
                                formatter={(value: number | undefined) => [`${value ?? 0} cliques`, 'Cliques']}
                                contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                              />
                              <Bar dataKey="clicks" fill="#3b82f6" radius={[6, 6, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Upload Funnel (Analyze Click → Role → File → Upload) */}
                  {metricsDashboard.upload_funnel && (
                    <div className="admin-card-body" style={{ marginBottom: '10px', border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', background: '#ffffff' }}>
                      <div style={{ padding: '13px 18px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                        <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>📤 Funil de Upload</h4>
                      </div>
                      <div style={{ padding: '20px' }}>
                        {/* Column headers */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid #f1f5f9' }}>
                          <div style={{ flex: '0 0 160px' }} />
                          <div style={{ flex: '1' }} />
                          <div style={{ flex: '0 0 44px', textAlign: 'right', fontSize: '11px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total</div>
                          <div style={{ flex: '0 0 72px', textAlign: 'right', fontSize: '11px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Conv.</div>
                          <div style={{ flex: '0 0 72px', textAlign: 'right', fontSize: '11px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Saída</div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          {[
                            { label: 'Clicou Analisar', value: metricsDashboard.upload_funnel.started, rate: 100, dropoff: 0 },
                            { label: 'Selecionou Role', value: metricsDashboard.upload_funnel.role_selected, rate: metricsDashboard.upload_funnel.conversion_rates.started_to_role, dropoff: metricsDashboard.upload_funnel.dropoff_rates.role_dropoff },
                            { label: 'Selecionou Arquivo', value: metricsDashboard.upload_funnel.file_selected, rate: metricsDashboard.upload_funnel.conversion_rates.role_to_file, dropoff: metricsDashboard.upload_funnel.dropoff_rates.file_dropoff },
                            { label: 'Upload Completo', value: metricsDashboard.upload_funnel.upload_completed, rate: metricsDashboard.upload_funnel.conversion_rates.file_to_upload, dropoff: metricsDashboard.upload_funnel.dropoff_rates.upload_dropoff },
                          ].map((stage, idx) => {
                            const barPct = Math.min(100, (stage.value / Math.max(metricsDashboard.upload_funnel.started, 1)) * 100);
                            const barColor = idx === 3 ? '#10b981' : idx === 0 ? '#3b82f6' : '#6366f1';
                            const dropColor = stage.dropoff > 30 ? '#dc2626' : stage.dropoff > 15 ? '#d97706' : '#64748b';
                            return (
                              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{ flex: '0 0 160px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>{stage.label}</div>
                                <div style={{ flex: '1', background: '#f1f5f9', borderRadius: '6px', height: '22px', position: 'relative', overflow: 'hidden' }}>
                                  <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${barPct}%`, background: barColor, opacity: 0.85, transition: 'width 0.4s ease', borderRadius: '6px' }} />
                                </div>
                                <div style={{ flex: '0 0 44px', textAlign: 'right', fontSize: '15px', fontWeight: '700', color: '#111827' }}>{stage.value}</div>
                                <div style={{ flex: '0 0 72px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#10b981' }}>{idx === 0 ? '—' : `${stage.rate.toFixed(1)}%`}</div>
                                <div style={{ flex: '0 0 72px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: dropColor }}>{idx === 0 ? '—' : `-${stage.dropoff.toFixed(1)}%`}</div>
                              </div>
                            );
                          })}
                        </div>
                        <div style={{ marginTop: '16px', padding: '10px 14px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span style={{ fontSize: '13px', color: '#374151' }}>Conversão total:</span>
                          <span style={{ fontSize: '16px', fontWeight: '700', color: metricsDashboard.upload_funnel.conversion_rates.overall >= 50 ? '#059669' : '#d97706' }}>
                            {metricsDashboard.upload_funnel.conversion_rates.overall.toFixed(1)}%
                          </span>
                          <span style={{ fontSize: '12px', color: '#64748b' }}>
                            ({metricsDashboard.upload_funnel.upload_completed} de {metricsDashboard.upload_funnel.started} completaram o upload)
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Anonymous Analysis Funnel */}
                  <div className="admin-card-body" style={{ marginBottom: '24px' }}>
                    <h4 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}>Analise Anonima</h4>
                    <div className="admin-grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
                      <div className="admin-card">
                        <span className="admin-metric-label">Sessoes Iniciadas</span>
                        <strong className="admin-metric-value">{metricsDashboard.behavior.anonymous.sessions_started}</strong>
                        <small style={{ display: 'block', marginTop: '4px', opacity: 0.7 }}>Uploads anonimos</small>
                      </div>
                      <div className="admin-card">
                        <span className="admin-metric-label">Sessoes Concluidas</span>
                        <strong className="admin-metric-value">{metricsDashboard.behavior.anonymous.sessions_completed}</strong>
                        <small style={{ display: 'block', marginTop: '4px', opacity: 0.7 }}>
                          {metricsDashboard.behavior.anonymous.completion_rate.toFixed(1)}% concluem
                        </small>
                      </div>
                      <div className="admin-card">
                        <span className="admin-metric-label">Convertidas em Cadastro</span>
                        <strong className="admin-metric-value">{metricsDashboard.behavior.anonymous.sessions_converted}</strong>
                        <small style={{ display: 'block', marginTop: '4px', opacity: 0.7 }}>
                          {metricsDashboard.behavior.anonymous.conversion_rate.toFixed(1)}% convertem
                        </small>
                      </div>
                      <div className="admin-card">
                        <span className="admin-metric-label">Sem Seguimento</span>
                        <strong className="admin-metric-value">{metricsDashboard.behavior.anonymous.sessions_no_followup}</strong>
                        <small style={{ display: 'block', marginTop: '4px', opacity: 0.7 }}>Nao viraram cadastro</small>
                      </div>
                      <div className="admin-card">
                        <span className="admin-metric-label">Conv. Análise→Cadastro</span>
                        <strong className="admin-metric-value">
                          {safeDivide(
                            metricsDashboard.behavior.anonymous.sessions_converted,
                            metricsDashboard.behavior.anonymous.sessions_completed,
                            100
                          ).toFixed(1)}%
                        </strong>
                        <small style={{ display: 'block', marginTop: '4px', opacity: 0.7 }}>
                          De sessões concluídas que viraram cadastro
                        </small>
                      </div>
                    </div>
                  </div>

                  {/* Paid Users */}
                  <div className="admin-card-body" style={{ marginBottom: '24px' }}>
                    <h4 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}>Assinaturas e Upgrades</h4>
                    <div className="admin-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
                      <div className="admin-card">
                        <span className="admin-metric-label">Usuarios Pagos (Total)</span>
                        <strong className="admin-metric-value">{metricsDashboard.behavior.paid.paid_users_total}</strong>
                        <small style={{ display: 'block', marginTop: '4px', opacity: 0.7 }}>Premium + Platinum</small>
                      </div>
                      <div className="admin-card">
                        <span className="admin-metric-label">Pagos no Periodo</span>
                        <strong className="admin-metric-value">{metricsDashboard.behavior.paid.paid_users_new}</strong>
                        <small style={{ display: 'block', marginTop: '4px', opacity: 0.7 }}>
                          {metricsDashboard.behavior.paid.paid_conversion_rate.toFixed(1)}% dos cadastros
                        </small>
                      </div>
                      <div className="admin-card">
                        <span className="admin-metric-label">Upgrades Confirmados</span>
                        <strong className="admin-metric-value">{metricsDashboard.behavior.paid.paid_upgrades}</strong>
                        <small style={{ display: 'block', marginTop: '4px', opacity: 0.7 }}>Eventos de pagamento</small>
                      </div>
                      <div className="admin-card">
                        <span className="admin-metric-label">Distribuicao de Planos</span>
                        <div style={{ marginTop: '6px', fontSize: '13px' }}>
                          Free: {metricsDashboard.behavior.tiers.free}
                        </div>
                        <div style={{ marginTop: '4px', fontSize: '13px' }}>
                          Premium: {metricsDashboard.behavior.tiers.premium} | Platinum: {metricsDashboard.behavior.tiers.platinum}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* MVP Success Metrics */}
                  <div className="admin-card-body" style={{ marginBottom: '24px' }}>
                    <h4 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}>Métricas de Sucesso do MVP</h4>
                    <div className="admin-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                      <div className="admin-card">
                        <span className="admin-metric-label">% Fazendo Upload</span>
                        <strong className="admin-metric-value" style={{
                          color: metricsDashboard.mvp_metrics.users_uploading_contract_pct >= metricsDashboard.goals.users_uploading_contract_target ? '#0c8b5c' : '#dc2626'
                        }}>
                          {metricsDashboard.mvp_metrics.users_uploading_contract_pct.toFixed(1)}%
                        </strong>
                        <small style={{ display: 'block', marginTop: '4px', opacity: 0.7 }}>Meta: {metricsDashboard.goals.users_uploading_contract_target}%</small>
                      </div>
                      <div className="admin-card">
                        <span className="admin-metric-label">% Fazendo Perguntas</span>
                        <strong className="admin-metric-value" style={{
                          color: metricsDashboard.mvp_metrics.users_asking_questions_pct >= metricsDashboard.goals.users_asking_questions_target ? '#0c8b5c' : '#dc2626'
                        }}>
                          {metricsDashboard.mvp_metrics.users_asking_questions_pct.toFixed(1)}%
                        </strong>
                        <small style={{ display: 'block', marginTop: '4px', opacity: 0.7 }}>Meta: {metricsDashboard.goals.users_asking_questions_target}%</small>
                      </div>
                      <div className="admin-card">
                        <span className="admin-metric-label">% Clicando em Preços</span>
                        <strong className="admin-metric-value" style={{
                          color: metricsDashboard.mvp_metrics.users_clicking_pricing_pct >= metricsDashboard.goals.users_clicking_pricing_target ? '#0c8b5c' : '#dc2626'
                        }}>
                          {metricsDashboard.mvp_metrics.users_clicking_pricing_pct.toFixed(1)}%
                        </strong>
                        <small style={{ display: 'block', marginTop: '4px', opacity: 0.7 }}>Meta: {metricsDashboard.goals.users_clicking_pricing_target}%</small>
                      </div>
                      <div className="admin-card">
                        <span className="admin-metric-label">% Dispostos a Pagar</span>
                        <strong className="admin-metric-value" style={{
                          color: metricsDashboard.mvp_metrics.users_willing_to_pay_pct >= metricsDashboard.goals.users_willing_to_pay_target ? '#0c8b5c' : '#dc2626'
                        }}>
                          {metricsDashboard.mvp_metrics.users_willing_to_pay_pct.toFixed(1)}%
                        </strong>
                        <small style={{ display: 'block', marginTop: '4px', opacity: 0.7 }}>Meta: {metricsDashboard.goals.users_willing_to_pay_target}%</small>
                      </div>
                      <div className="admin-card">
                        <span className="admin-metric-label">Confiança Média da IA</span>
                        <strong className="admin-metric-value" style={{
                          color: metricsDashboard.mvp_metrics.average_ai_confidence >= metricsDashboard.goals.average_ai_confidence_target ? '#0c8b5c' : '#dc2626'
                        }}>
                          {(metricsDashboard.mvp_metrics.average_ai_confidence * 100).toFixed(0)}%
                        </strong>
                        <small style={{ display: 'block', marginTop: '4px', opacity: 0.7 }}>Meta: {(metricsDashboard.goals.average_ai_confidence_target * 100).toFixed(0)}%</small>
                      </div>
                      <div className="admin-card">
                        <span className="admin-metric-label">Taxa de Conclusão</span>
                        <strong className="admin-metric-value" style={{
                          color: metricsDashboard.mvp_metrics.analysis_completion_rate_pct >= metricsDashboard.goals.analysis_completion_rate_target ? '#0c8b5c' : '#dc2626'
                        }}>
                          {metricsDashboard.mvp_metrics.analysis_completion_rate_pct.toFixed(1)}%
                        </strong>
                        <small style={{ display: 'block', marginTop: '4px', opacity: 0.7 }}>Meta: {metricsDashboard.goals.analysis_completion_rate_target}%</small>
                      </div>
                    </div>
                  </div>

                  {/* Conversion Funnel */}
                  <div style={{ marginBottom: '10px', border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', background: '#ffffff' }}>
                    <div style={{ padding: '13px 18px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                      <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>🔄 Funil de Conversão Geral</h4>
                    </div>
                    <div style={{ padding: '20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid #f1f5f9' }}>
                        <div style={{ flex: '0 0 180px' }} />
                        <div style={{ flex: '1' }} />
                        <div style={{ flex: '0 0 60px', textAlign: 'right', fontSize: '11px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total</div>
                        <div style={{ flex: '0 0 90px', textAlign: 'right', fontSize: '11px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Próx. etapa</div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {[
                          { label: 'Visitantes', value: metricsDashboard.funnel.visitors, rate: null },
                          { label: 'Cadastros', value: metricsDashboard.funnel.signups, rate: metricsDashboard.funnel.conversion_rates.visitor_to_signup },
                          { label: 'Uploads', value: metricsDashboard.funnel.uploads, rate: metricsDashboard.funnel.conversion_rates.signup_to_upload },
                          { label: 'Perguntas', value: metricsDashboard.funnel.questions, rate: metricsDashboard.funnel.conversion_rates.upload_to_question },
                          { label: 'Cliques em Preços', value: metricsDashboard.funnel.pricing_clicks, rate: metricsDashboard.funnel.conversion_rates.question_to_pricing },
                          { label: 'Pagamentos', value: metricsDashboard.funnel.payments, rate: metricsDashboard.funnel.conversion_rates.pricing_to_payment },
                        ].map((stage, idx) => {
                          const barPct = metricsDashboard.funnel.visitors > 0 ? Math.min(100, (stage.value / metricsDashboard.funnel.visitors) * 100) : 0;
                          const colors = ['#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#f59e0b', '#10b981'];
                          return (
                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <div style={{ flex: '0 0 180px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>{stage.label}</div>
                              <div style={{ flex: '1', background: '#f1f5f9', borderRadius: '6px', height: '22px', position: 'relative', overflow: 'hidden' }}>
                                <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${barPct}%`, background: colors[idx], opacity: 0.85, transition: 'width 0.4s ease', borderRadius: '6px' }} />
                              </div>
                              <div style={{ flex: '0 0 60px', textAlign: 'right', fontSize: '15px', fontWeight: '700', color: '#111827' }}>{stage.value}</div>
                              <div style={{ flex: '0 0 90px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: stage.rate === null ? '#94a3b8' : stage.rate >= 50 ? '#10b981' : stage.rate >= 20 ? '#d97706' : '#dc2626' }}>
                                {stage.rate === null ? '—' : `${stage.rate.toFixed(1)}%`}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div style={{ marginTop: '16px', padding: '10px 14px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                        <span style={{ fontSize: '13px', color: '#374151' }}>Conversão geral: </span>
                        <span style={{ fontSize: '15px', fontWeight: '700', color: '#059669' }}>{metricsDashboard.funnel.conversion_rates.overall_conversion.toFixed(2)}%</span>
                        <span style={{ fontSize: '12px', color: '#64748b', marginLeft: '8px' }}>dos visitantes se tornam pagantes</span>
                      </div>
                    </div>
                  </div>

                  {/* AI Quality & Failures */}
                  <div className="admin-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                    <div className="admin-card">
                      <div className="admin-card-header">
                        <h4>Qualidade da IA</h4>
                      </div>
                      <div style={{ padding: '16px' }}>
                        <div style={{ marginBottom: '12px' }}>
                          <div style={{ fontSize: '14px', opacity: 0.7 }}>Perguntas Totais</div>
                          <div style={{ fontSize: '24px', fontWeight: '600' }}>{metricsDashboard.ai_quality.total_questions_asked}</div>
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                          <div style={{ fontSize: '14px', opacity: 0.7 }}>Análises Completas</div>
                          <div style={{ fontSize: '24px', fontWeight: '600' }}>
                            {metricsDashboard.ai_quality.total_analyses_completed} / {metricsDashboard.ai_quality.total_analyses_started}
                          </div>
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                          <div style={{ fontSize: '14px', opacity: 0.7 }}>Nota Média dos Usuários</div>
                          <div style={{ fontSize: '24px', fontWeight: '600' }}>{metricsDashboard.ai_quality.average_user_rating.toFixed(2)}/5.00</div>
                        </div>
                      </div>
                    </div>

                  </div>
                </>
              ) : (
                <div className="admin-empty">Nenhum dado disponível para o período selecionado</div>
              )}
            </section>
          </>
        )}

        {activeTab === 'errors' && (
          <section className="admin-card admin-panel">
            <div className="admin-card-header">
              <h3>Monitoramento de Erros</h3>
            </div>
            {!failureMetrics && <div className="admin-loading">Carregando erros...</div>}
            {failureMetrics && (
              <>
                {renderFailureBreakdown()}
                {failureMetrics.recent_failures && failureMetrics.recent_failures.length > 0 && (
                  <div className="admin-card" style={{ marginTop: '16px' }}>
                    <div className="admin-card-header">
                      <h4>Erros recentes</h4>
                    </div>
                    <div className="admin-table" style={{ marginTop: '8px' }}>
                      <table>
                        <thead>
                          <tr>
                            <th>Data</th>
                            <th>Documento</th>
                            <th>Usuário</th>
                            <th>Motivo</th>
                          </tr>
                        </thead>
                        <tbody>
                          {failureMetrics.recent_failures.map((row, idx) => (
                            <tr key={idx}>
                              <td>{row.occurred_at ? formatDate(row.occurred_at) : '—'}</td>
                              <td>{row.document_id ?? '—'}</td>
                              <td>{row.user_id ?? '—'}</td>
                              <td>{row.reason || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                <div className="admin-card" style={{ marginTop: '16px' }}>
                  <div className="admin-card-header">
                    <h4>Descrição de erros (logs)</h4>
                    <span className="pill badge-error">ERROR</span>
                  </div>
                  {(!failureMetrics.error_logs || failureMetrics.error_logs.length === 0) && (
                    <div className="admin-empty">Nenhum erro encontrado no log.</div>
                  )}
                  {failureMetrics.error_logs && failureMetrics.error_logs.length > 0 && (
                    <div className="admin-table admin-error-table" style={{ marginTop: '8px' }}>
                      <table>
                        <thead>
                          <tr>
                            <th>Timestamp</th>
                            <th>Origem</th>
                            <th>Código</th>
                            <th>Descrição</th>
                          </tr>
                        </thead>
                        <tbody>
                          {failureMetrics.error_logs.map((row, idx) => (
                            <React.Fragment key={`${row.timestamp}-${idx}`}>
                              <tr className={row.stack ? 'has-stack' : ''}>
                                <td>{formatDateTime(row.timestamp || '')}</td>
                                <td>{row.logger || 'app'}</td>
                                <td>
                                  <span className={`pill ${row.code ? 'pill-danger' : 'pill-muted'}`}>
                                    {row.code || '—'}
                                  </span>
                                </td>
                                <td>
                                  <div className="error-description">
                                    <span>{row.message}</span>
                                    {row.stack && (
                                      <button
                                        type="button"
                                        className="link-button"
                                        onClick={() => toggleErrorRow(idx)}
                                      >
                                        {expandedErrorRows.has(idx) ? 'Esconder stack' : 'Ver stack'}
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                              {row.stack && expandedErrorRows.has(idx) && (
                                <tr className="stack-row">
                                  <td colSpan={4}>
                                    <pre className="stack-snippet">{row.stack}</pre>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                <div className="admin-card" style={{ marginTop: '16px' }}>
                  <div className="admin-card-header">
                    <h4>Logs em tempo real</h4>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <button
                        className={`admin-button secondary small ${logLevelFilter ? 'active' : ''}`}
                        onClick={() => setLogLevelFilter((prev) => (prev ? undefined : 'ERROR'))}
                        type="button"
                        aria-pressed={!!logLevelFilter}
                      >
                        {logLevelFilter ? 'Mostrar todos' : 'Filtrar ERROR'}
                      </button>
                      <label className="admin-select-label">
                        Linhas:
                        <select
                          value={logLimit}
                          onChange={(e) => setLogLimit(Number(e.target.value))}
                          className="admin-select"
                        >
                          <option value={100}>100</option>
                          <option value={200}>200</option>
                          <option value={500}>500</option>
                        </select>
                      </label>
                      <button className="admin-button secondary small" onClick={loadLogs} disabled={loadingLogs}>
                        {loadingLogs ? 'Carregando...' : 'Atualizar logs'}
                      </button>
                    </div>
                  </div>
                  <div className="logs-pane">
                    {loadingLogs ? (
                      <div className="admin-loading">Carregando logs...</div>
                    ) : (
                      <pre className="logs-content">
                        {(logLines || []).slice(-logLimit).join('\n')}
                      </pre>
                    )}
                  </div>
                </div>
                <p style={{ marginTop: '16px', color: '#475569', fontSize: '13px' }}>
                  Fonte: falhas registradas (inclui uploads, pagamentos, abandonos e razões específicas).
                </p>
              </>
            )}
          </section>
        )}

      </div>

      {showSettingsModal && (
        <div className="admin-modal-overlay" onClick={closeSettingsModal}>
          <div className="admin-modal model-settings-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <div className="admin-modal-title">Configuração de Modelos de IA</div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button className="admin-button secondary small" onClick={closeSettingsModal}>Fechar</button>
              </div>
            </div>
            <div className="admin-modal-body">
              {loadingModelConfig && <div className="admin-loading">Carregando configurações...</div>}
              {!loadingModelConfig && (
                <div className="model-settings">
                {modelSections.map((section) => (
                  <div key={section.title} className="model-settings__section">
                    <div className="model-settings__section-title">{section.title}</div>
                    <div className="model-settings__grid">
                      {section.items.map((field) => (
                        <div className="model-settings__card" key={field.key}>
                          <div className="model-settings__card-head">
                            <span className="model-settings__card-title">{field.label}</span>
                            {(() => {
                              const defaultModel = defaultLabelFor(field.key);
                              const options = modelOptions.filter((o) => o !== defaultModel);
                              return (
                                <select
                                  className="admin-select"
                                  value={(modelConfigForm[field.key] as string | undefined) ?? ''}
                                  onChange={(e) => handleModelChange(field.key, e.target.value)}
                                  disabled={savingModelConfig}
                                >
                                  <option value="">{defaultModel}</option>
                                  {options.map((option) => (
                                    <option key={option} value={option}>
                                      {option}
                                    </option>
                                  ))}
                                </select>
                              );
                            })()}
                          </div>
                          <p className="model-settings__card-helper">{field.helper}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                <div className="model-settings__footer">
                  <div className="model-settings__meta">
                    <span>Última atualização: {aiModelConfig?.updated_at ? formatDate(aiModelConfig.updated_at) : '—'}</span>
                  </div>
                    <div className="model-settings__actions">
                      <button
                        className="admin-button secondary"
                        onClick={handleResetModelConfig}
                        disabled={savingModelConfig || !hasModelChanges}
                      >
                        Limpar (usar padrão)
                      </button>
                      <button
                        className="admin-button admin-button-primary"
                        onClick={handleSaveModelConfig}
                        disabled={savingModelConfig || !hasModelChanges}
                      >
                        {savingModelConfig ? 'Salvando...' : 'Salvar modelos'}
                      </button>
                    </div>
                  </div>
                  {modelConfigMessage && <div className="model-settings__toast">{modelConfigMessage}</div>}
                  <div className="model-settings__legend">
                    <div className="model-settings__legend-head">
                      <strong>Ordem de capacidade/custo</strong>
                      <span className="pill">mais forte → mais leve</span>
                    </div>
                    <ul className="model-settings__legend-list">
                      <li>
                        <span className="legend-rank">1.</span>
                        <span className="model-name">gpt-4.1</span>
                        <span className="legend-note">melhor qualidade e raciocínio; use em análises críticas ou documentos complexos</span>
                      </li>
                      <li>
                        <span className="legend-rank">2.</span>
                        <span className="model-name">gpt-4o</span>
                        <span className="legend-note">equilíbrio custo/velocidade; bom padrão geral</span>
                      </li>
                      <li>
                        <span className="legend-rank">3.</span>
                        <span className="model-name">o1-mini</span>
                        <span className="legend-note">raciocínio leve; bom para prompts menores com alguma cadeia lógica</span>
                      </li>
                      <li>
                        <span className="legend-rank">4.</span>
                        <span className="model-name">gpt-4.1-mini</span>
                        <span className="legend-note">rápido e barato; bom para respostas rápidas onde precisão extra não é vital</span>
                      </li>
                      <li>
                        <span className="legend-rank">5.</span>
                        <span className="model-name">gpt-4o-mini</span>
                        <span className="legend-note">mais rápido/mais barato; use para volume alto e tolerância a menor qualidade</span>
                      </li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {attachmentViewerOpen && attachmentViewerUrl && (
        <div className="admin-modal-overlay" onClick={closeAttachmentViewer}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <div className="admin-modal-title">{attachmentViewerFilename}</div>
              <button className="admin-button secondary small" onClick={closeAttachmentViewer}>
                Fechar
              </button>
            </div>
            <div className="admin-modal-body">
              {attachmentViewerIsImage ? (
                <img className="admin-attachment-image" src={attachmentViewerUrl} alt={attachmentViewerFilename} />
              ) : (
                <div className="admin-attachment-file">
                  <p>Arquivo anexado.</p>
                  <a className="admin-button secondary" href={attachmentViewerUrl} download={attachmentViewerFilename}>
                    Baixar
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
